import { BarElement, CategoryScale, ChartData, Chart as ChartJS, ChartOptions, Legend, LineElement, LinearScale, Plugin, PointElement, TimeScale, Title, Tooltip } from 'chart.js';
import { getRelativePosition } from 'chart.js/helpers';
import 'chartjs-adapter-moment';
import zoomPlugin from 'chartjs-plugin-zoom';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { getChartOverlays, getIssuesAnnotations, getODKSubmissions, getPrefetchedTimeSeriesData, getTahmoQCFlagged, getTimeSeriesData, recordUserActivity } from '../../services/api.service';
import { AnnotationConfig, AnnotationPeriod } from '../../types/annotation_period.type';
import { DripClogPeriod } from '../../types/drip_clog.type';
import { IssueAnnotation, IssueAnnotationsResponse } from '../../types/issue_annotation.type';
import { ODKSubmission } from '../../types/odk_submission.type';
import IReport from '../../types/report.type';

// QC maps for acronyms and severity labels
const QC_ACRONYM_MAP: Record<string, string> = {
	mc: 'Manual check',
	rc: 'Range climate',
	rs: 'Range sensor',
	td: 'Temporal delta',
	ti: 'Temporal sigma',
	ts: 'Temporal step',
};
const QC_SEVERITY_MAP: Record<number, string> = {
	4: 'erroneous',
	3: 'doubtful',
	2: 'inconsistent',
};

// Brief explanations for each QC check (for tooltips)
const QC_EXPLANATION_MAP: Record<string, string> = {
	mc: 'Manual review performed by an operator',
	rc: 'Value outside the expected climatological range for the location/season',
	rs: 'Value outside the plausible sensor or physical range',
	td: 'Change between consecutive measurements is unusually large',
	ti: 'Value deviates strongly from recent variability (standard deviation check)',
	ts: 'Change of Value between consecutive measurements exceeds limits',
};

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, TimeScale, zoomPlugin);

const TIME_SERIES_CACHE_VERSION = 'v2';

interface TimeSeriesDataPoint {
	timestamp: string;
	value: number | null;
	// Optional QC metrics available for raw aggregation
	quality?: number | null;
	rc?: number | null;
	rs?: number | null;
	mc?: number | null;
	td?: number | null;
	ti?: number | null;
	ts?: number | null;
}

interface TimeSeriesData {
	data: TimeSeriesDataPoint[];
	metadata: {
		station_id: string;
		variable: string;
		start_date: string;
		end_date: string;
		count: number;
		aggregation: string;
		total_count: number;
		time_range_days?: number;
		additional_sensors?: Array<{ sensor: string; name: string }> | null;
		has_multiple_sensors?: boolean;
	};
}

interface QCPeriod {
	start: string;
	end: string;
	text: string;
	count: number;
	variable: string;
}

interface ColocatedStationData {
	stationId: string;
	liveStationId?: string;
	name: string;
	color?: string;
}

interface SimpleTimeSeriesChartProps {
	stationId: string;
	liveStationId?: string;
	variable: string;
	startDate: string;
	endDate: string;
	height?: number;
	title?: string;
	yAxisLabel?: string;
	aggregation?: 'raw' | 'daily' | 'hourly' | 'auto';
	chartType?: 'line' | 'bar';
	color?: string;
	backgroundColor?: string;
	showDataInfo?: boolean;
	enableZoom?: boolean;
	plugins?: Plugin<'line' | 'bar'>[];
	showDataGaps?: boolean;
	showQCFlags?: boolean;
	showIssueAnnotations?: boolean;
	showMaintenanceRecordsAnnotations?: boolean;
	annotationConfigs?: AnnotationConfig[];
	showODKSubmissions?: boolean;
	// Sync options: when enabled, this chart will broadcast and listen for zoom/pan updates
	syncEnabled?: boolean;
	syncKey?: string; // charts with the same key will sync together
	// Colocated stations to display on the same chart
	colocatedStations?: ColocatedStationData[];
	// Additional variables to overlay in the same chart (same station)
	comparisonVariables?: string[];
	onBusyStateChange?: (busy: boolean) => void;
}

const timeSeriesRequestCache = new Map<string, Promise<TimeSeriesData>>();

// sessionStorage helpers — persist resolved chart data across navigation
// Key format: ts_cache|version|stationId|variable|startDate|endDate|aggregation
// TTL: 10 minutes (stored alongside data)
const SESSION_CACHE_TTL_MS = 10 * 60 * 1000;

const getSessionCacheKey = (
	stationId: string,
	variable: string,
	startDate: string,
	endDate: string,
	aggregation?: string,
	maxPoints?: number
) => `ts_cache|${TIME_SERIES_CACHE_VERSION}|${stationId}|${variable}|${startDate}|${endDate}|${aggregation || 'raw'}|${maxPoints || 'full'}`;

const getFromSessionCache = (
	stationId: string,
	variable: string,
	startDate: string,
	endDate: string,
	aggregation?: string,
	maxPoints?: number
): TimeSeriesData | null => {
	try {
		const key = getSessionCacheKey(stationId, variable, startDate, endDate, aggregation, maxPoints);
		const raw = sessionStorage.getItem(key);
		if (!raw) return null;
		const { data, timestamp } = JSON.parse(raw);
		if (Date.now() - timestamp > SESSION_CACHE_TTL_MS) {
			sessionStorage.removeItem(key);
			return null;
		}
		return data as TimeSeriesData;
	} catch {
		return null;
	}
};

const saveToSessionCache = (
	stationId: string,
	variable: string,
	startDate: string,
	endDate: string,
	aggregation: string | undefined,
	maxPoints: number | undefined,
	data: TimeSeriesData
): void => {
	try {
		const key = getSessionCacheKey(stationId, variable, startDate, endDate, aggregation, maxPoints);
		sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
	} catch {
		// sessionStorage may be full or unavailable — ignore silently
	}
};

const buildTimeSeriesCacheKey = (
	stationId: string,
	variable: string,
	startDate: string,
	endDate: string,
	aggregation?: string,
	maxPoints?: number
) => `${TIME_SERIES_CACHE_VERSION}|${stationId}|${variable}|${startDate}|${endDate}|${aggregation || 'raw'}|${maxPoints || 'full'}`;

const getTimeSeriesDataCached = (
	stationId: string,
	variable: string,
	startDate: string,
	endDate: string,
	aggregation?: string,
	maxPoints?: number
) => {
	const key = buildTimeSeriesCacheKey(stationId, variable, startDate, endDate, aggregation, maxPoints);

	// 1. Check local dedup cache (in-flight deduplication)
	const existing = timeSeriesRequestCache.get(key);
	if (existing) {
		return existing;
	}

	// 2. Check sessionStorage cache (instant load when revisiting same chart)
	const sessionData = getFromSessionCache(stationId, variable, startDate, endDate, aggregation, maxPoints);
	if (sessionData) {
		const resolved = Promise.resolve(sessionData);
		timeSeriesRequestCache.set(key, resolved);
		setTimeout(() => timeSeriesRequestCache.delete(key), 5 * 60 * 1000);
		return resolved;
	}

	// 3. Check the global prefetch cache (populated on row hover)
	const prefetched = !maxPoints ? getPrefetchedTimeSeriesData(stationId, variable, startDate, endDate, aggregation || 'auto') : undefined;
	if (prefetched) {
			const withSave = prefetched.then((data) => {
				saveToSessionCache(stationId, variable, startDate, endDate, aggregation, maxPoints, data);
				return data;
			});
		timeSeriesRequestCache.set(key, withSave as Promise<TimeSeriesData>);
		withSave.finally(() => {
			setTimeout(() => timeSeriesRequestCache.delete(key), 5 * 60 * 1000);
		});
		return withSave as Promise<TimeSeriesData>;
	}

	// 4. Fetch fresh — save to sessionStorage on success
	const request = (getTimeSeriesData(stationId, variable, startDate, endDate, aggregation, maxPoints) as Promise<TimeSeriesData>)
			.then((data) => {
				saveToSessionCache(stationId, variable, startDate, endDate, aggregation, maxPoints, data);
				return data;
			});
	timeSeriesRequestCache.set(key, request);
	request.finally(() => {
		setTimeout(() => timeSeriesRequestCache.delete(key), 5 * 60 * 1000);
	});
	return request;
};

const SimpleTimeSeriesChart: React.FC<SimpleTimeSeriesChartProps> = ({
	stationId,
	liveStationId,
	variable,
	startDate,
	endDate,
	height = 400,
	title,
	yAxisLabel,
	aggregation = 'auto',
	chartType = 'line',
	color = '#1f77b4',
	backgroundColor,
	showDataInfo = true,
	enableZoom = true,
	plugins = [],
	showDataGaps = false,
	showQCFlags = false,
	annotationConfigs = [],
	showIssueAnnotations = false,
	showMaintenanceRecordsAnnotations = false,
	showODKSubmissions = false,
	syncEnabled = false,
	syncKey,
	colocatedStations = [],
	comparisonVariables = [],
	onBusyStateChange,
}) => {
	const MAINTENANCE_CDN_BASE = 'https://assetreports.s3.eu-gb.cloud-object-storage.appdomain.cloud';
	// Parse annotation dates defensively; upstream systems may return mixed formats.
	const parseChartDate = (raw: any): moment.Moment | null => {
		if (!raw) return null;
		const direct = moment(raw);
		if (direct.isValid()) return direct;
		const zoned = moment.parseZone(raw);
		if (zoned.isValid()) return zoned;
		return null;
	};

	const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	// Stale-while-revalidate: track background refresh separately so old chart stays visible
	const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [qcPeriods, setQCPeriods] = useState<QCPeriod[]>([]);
	const [dripClogPeriods, setDripClogPeriods] = useState<DripClogPeriod[]>([]);
	const [qcLoading, setQCLoading] = useState<boolean>(false);
	const [issueAnnotations, setIssueAnnotations] = useState<IssueAnnotation[]>([]);
	const [issueAnnotationsLoading, setIssueAnnotationsLoading] = useState<boolean>(false);
	const [maintenanceReportAnnotation, setMaintenanceReportAnnotation] = useState<IReport[]>([]);
	const [maintenanceReportAnnotationLoading, setMaintenanceReportAnnotationLoading] = useState<boolean>(false);

	// Colocated stations data
	const [colocatedStationsData, setColocatedStationsData] = useState<Record<string, TimeSeriesData | null>>({});

	// Additional precipitation sensors data
	const [additionalSensorsData, setAdditionalSensorsData] = useState<Record<string, TimeSeriesData | null>>({});
	const [comparisonVariableData, setComparisonVariableData] = useState<Record<string, TimeSeriesData | null>>({});

	// Inside the component, add state for generic annotations
	const [annotationPeriods, setAnnotationPeriods] = useState<Record<string, AnnotationPeriod[]>>({});
	const [annotationLoading, setAnnotationLoading] = useState<Record<string, boolean>>({});

	// Add state variables (after other state declarations)
	const [odkSubmissions, setODKSubmissions] = useState<ODKSubmission[]>([]);
	const [odkSubmissionsLoading, setODKSubmissionsLoading] = useState<boolean>(false);
	// Modal state for ODK submission detail view
	const [odkModalSubmission, setOdkModalSubmission] = useState<ODKSubmission | null>(null);
	const [odkModalHydrating, setOdkModalHydrating] = useState<boolean>(false);
	const [issueModalAnnotation, setIssueModalAnnotation] = useState<IssueAnnotation | null>(null);
	const [maintenanceModalReport, setMaintenanceModalReport] = useState<IReport | null>(null);
	// Triggers overlay fetch after first successful main-series load.
	const [overlayReadyTick, setOverlayReadyTick] = useState<number>(0);
	// chartKey forces a full Chart.js canvas remount when annotation visibility toggles off.
	// react-chartjs-2 calls chart.update() internally which does NOT un-register inline plugins,
	// so simply removing a plugin from the array won't hide its drawings. Remounting fixes this.
	const [chartKey, setChartKey] = useState<number>(0);

	const issueThreadForModal = useMemo(() => {
		if (!issueModalAnnotation) return { title: '', issue: null as IssueAnnotation | null, comments: [] as IssueAnnotation[] };
		const threadTitle = issueModalAnnotation.type === 'comment'
			? (issueModalAnnotation.title || '').replace(/^Comment on:\s*/i, '').trim()
			: (issueModalAnnotation.title || '').trim();
		const issue = issueAnnotations.find((a) => a.type === 'issue' && (a.title || '').trim() === threadTitle) || null;
		let comments = issueAnnotations
			.filter((a) => {
				if (a.type !== 'comment') return false;
				const t = (a.title || '').trim();
				return t === `Comment on: ${threadTitle}` || t.replace(/^Comment on:\s*/i, '').trim() === threadTitle;
			})
			.sort((a, b) => moment((a as any).date).valueOf() - moment((b as any).date).valueOf());

		// If user clicked a comment marker, show thread only up to that comment (inclusive).
		if (issueModalAnnotation.type === 'comment') {
			const clickedTs = moment((issueModalAnnotation as any).date).valueOf();
			if (Number.isFinite(clickedTs)) {
				comments = comments.filter((c) => {
					const ts = moment((c as any).date).valueOf();
					return Number.isFinite(ts) ? ts <= clickedTs : true;
				});
			}
		}
		return { title: threadTitle, issue, comments };
	}, [issueModalAnnotation, issueAnnotations]);

	const chartRef = useRef<any>(null);
	const syncIdRef = useRef<string>(Math.random().toString(36).slice(2));
	const overlayFetchStateRef = useRef<
		Record<
			string,
			{
				issues: boolean;
				reports: boolean;
				odk: boolean;
				annotationTypes: Set<string>;
			}
		>
	>({});
	// Tracks whether the main series has loaded at least once — used to gate the
	// overlay fetch without putting timeSeriesData in the dependency array (which
	// would cause overlays to re-fetch on every stale-while-revalidate refresh).
	const mainSeriesEverLoadedRef = useRef<boolean>(false);
	const suppressBroadcastRef = useRef<boolean>(false);

	const SYNC_EVENT = 'chartSync_zoom';
	// Global last-range cache key on window to sync newly added charts
	const LAST_RANGE_CACHE_KEY = '__chartSyncLastRange';

	type XRange = { min: number; max: number };

	const setLastRange = (key?: string, range?: XRange) => {
		if (!key || !range) return;
		const w: any = window as any;
		if (!w[LAST_RANGE_CACHE_KEY]) w[LAST_RANGE_CACHE_KEY] = {};
		w[LAST_RANGE_CACHE_KEY][key] = range;
	};

	const getLastRange = (key?: string): XRange | undefined => {
		if (!key) return undefined;
		const w: any = window as any;
		const store = w[LAST_RANGE_CACHE_KEY] || {};
		const r = store[key];
		if (r && typeof r.min === 'number' && typeof r.max === 'number') return r as XRange;
		return undefined;
	};

	// Apply external zoom/pan range to this chart
	const applyExternalRange = (min: number, max: number) => {
		const chartInstance = chartRef.current && (chartRef.current as any).chart ? (chartRef.current as any).chart : chartRef.current;
		if (!chartInstance) {
			return;
		}
		try {
			// Use zoom plugin's API to set the range
			if (chartInstance.zoomScale) {
				chartInstance.zoomScale('x', { min, max }, 'none');
			} else {
				// Fallback to direct scale manipulation
				const xScale = chartInstance.scales?.x;
				if (xScale) {
					xScale.options.min = min;
					xScale.options.max = max;
					chartInstance.update('none');
				}
			}
		} catch (e) {
			console.error('Failed to apply external range:', e);
		}
	};

	// Listen for sync events
	useEffect(() => {
		if (!syncEnabled || !syncKey) return;
		const handler = (e: Event) => {
			const ce = e as CustomEvent<any>;
			const detail = ce.detail || {};
			if (!detail || detail.syncKey !== syncKey) return;
			if (detail.sourceId === syncIdRef.current) return; // ignore own events
			if (typeof detail.min !== 'number' || typeof detail.max !== 'number') return;
			// Apply the range received from other charts
			setTimeout(() => applyExternalRange(detail.min, detail.max), 50);
		};
		window.addEventListener(SYNC_EVENT, handler as EventListener);

		// If there is a cached last range for this syncKey (from another chart), apply it with a delay
		// to ensure the chart is fully mounted and rendered
		const cached = getLastRange(syncKey);
		if (cached && typeof cached.min === 'number' && typeof cached.max === 'number') {
			const timer = setTimeout(() => {
				applyExternalRange(cached.min, cached.max);
			}, 300);
			return () => {
				clearTimeout(timer);
				window.removeEventListener(SYNC_EVENT, handler as EventListener);
			};
		}

		return () => window.removeEventListener(SYNC_EVENT, handler as EventListener);
	}, [syncEnabled, syncKey]);

	// Removed automatic broadcast on mount/update - broadcasts now only happen on user zoom/pan actions

	useEffect(() => {
		onBusyStateChange?.(loading || isRefreshing);
	}, [loading, isRefreshing, onBusyStateChange]);

	// If a toggle is turned off, clear its loading state immediately so captions don't show stale loaders.
	useEffect(() => {
		if (!showIssueAnnotations) setIssueAnnotationsLoading(false);
	}, [showIssueAnnotations]);

	useEffect(() => {
		if (!showMaintenanceRecordsAnnotations) setMaintenanceReportAnnotationLoading(false);
	}, [showMaintenanceRecordsAnnotations]);

	useEffect(() => {
		if (!showODKSubmissions) setODKSubmissionsLoading(false);
	}, [showODKSubmissions]);

	// Batched overlay fetch to avoid request fan-out.
	useEffect(() => {
		const annotationTypes = Array.from(new Set((annotationConfigs || []).map((config) => config.type))).sort();
		const wantsIssues = showIssueAnnotations;
		const wantsReports = showMaintenanceRecordsAnnotations && !!liveStationId;
		const wantsOdk = showODKSubmissions;
		const wantsAnnotations = annotationTypes.length > 0;
		const mainSeriesLoaded = mainSeriesEverLoadedRef.current;
		const wantsAnything = wantsIssues || wantsReports || wantsOdk || wantsAnnotations;

		if (!wantsAnything || !mainSeriesLoaded) return;

		const overlayKey = `${stationId}|${startDate}|${endDate}|${liveStationId || ''}|${annotationTypes.join(',')}`;
		let fetchState = overlayFetchStateRef.current[overlayKey];
		if (!fetchState) {
			fetchState = {
				issues: false,
				reports: false,
				odk: false,
				annotationTypes: new Set<string>(),
			};
			overlayFetchStateRef.current[overlayKey] = fetchState;
		}

		const needsIssues = wantsIssues && !fetchState.issues;
		const needsReports = wantsReports && !fetchState.reports;
		const needsOdk = wantsOdk && !fetchState.odk;
		const missingAnnotationTypes = wantsAnnotations ? annotationTypes.filter((type) => !fetchState.annotationTypes.has(type)) : [];

		if (!needsIssues && !needsReports && !needsOdk && missingAnnotationTypes.length === 0) return;

		// Mark as fetched optimistically to prevent duplicate in-flight requests from toggle churn.
		if (needsIssues) fetchState.issues = true;
		if (needsReports) fetchState.reports = true;
		if (needsOdk) fetchState.odk = true;
		missingAnnotationTypes.forEach((type) => fetchState.annotationTypes.add(type));

		if (needsIssues) setIssueAnnotationsLoading(true);
		if (needsReports) setMaintenanceReportAnnotationLoading(true);
		if (needsOdk) setODKSubmissionsLoading(true);
		if (missingAnnotationTypes.length > 0) {
			const initLoading: Record<string, boolean> = {};
			missingAnnotationTypes.forEach((type) => {
				initLoading[type] = true;
			});
			setAnnotationLoading((prev) => ({ ...prev, ...initLoading }));
		}

		const fetchOverlays = async () => {
			try {
				const overlay = await getChartOverlays(stationId, startDate, endDate, {
					liveStationId,
					includeIssues: needsIssues,
					includeReports: needsReports,
					includeOdk: needsOdk,
					annotationTypes: missingAnnotationTypes,
				});

				if (needsIssues) {
					const overlayIssues = (overlay.issues || []) as IssueAnnotation[];
					if (overlayIssues.length > 0) {
						setIssueAnnotations(overlayIssues);
					} else {
						// Fallback to the legacy endpoint to avoid losing issues/comments if
						// the combined overlays response is empty or delayed.
						try {
							const issueResponse: IssueAnnotationsResponse = await getIssuesAnnotations(stationId, startDate, endDate);
							setIssueAnnotations(issueResponse.data || []);
						} catch {
							setIssueAnnotations([]);
						}
					}
				}
				if (needsReports) setMaintenanceReportAnnotation((overlay.reports || []) as IReport[]);
				if (needsOdk) setODKSubmissions((overlay.odkSubmissions || []) as ODKSubmission[]);
				if (missingAnnotationTypes.length > 0) {
					const incomingRaw = (overlay.annotationPeriods || {}) as Record<string, any>;
					const incoming: Record<string, AnnotationPeriod[]> = {};
					Object.entries(incomingRaw).forEach(([k, v]) => {
						if (Array.isArray(v)) {
							incoming[k] = v as AnnotationPeriod[];
						} else if (v && typeof v === 'object') {
							incoming[k] = Object.values(v) as AnnotationPeriod[];
						} else {
							incoming[k] = [];
						}
					});
					setAnnotationPeriods((prev) => ({ ...prev, ...incoming }));
				}
			} catch (err) {
				console.error('Failed to fetch chart overlays:', err);
				// Allow retry on future effect run if request failed.
				if (needsIssues) fetchState.issues = false;
				if (needsReports) fetchState.reports = false;
				if (needsOdk) fetchState.odk = false;
				missingAnnotationTypes.forEach((type) => fetchState.annotationTypes.delete(type));

				if (needsIssues) {
					// Fallback when combined endpoint fails.
					try {
						const issueResponse: IssueAnnotationsResponse = await getIssuesAnnotations(stationId, startDate, endDate);
						setIssueAnnotations(issueResponse.data || []);
						fetchState.issues = true;
					} catch {
						fetchState.issues = false;
					}
				}
			} finally {
				if (needsIssues) setIssueAnnotationsLoading(false);
				if (needsReports) setMaintenanceReportAnnotationLoading(false);
				if (needsOdk) setODKSubmissionsLoading(false);
				if (missingAnnotationTypes.length > 0) {
					const doneLoading: Record<string, boolean> = {};
					missingAnnotationTypes.forEach((type) => {
						doneLoading[type] = false;
					});
					setAnnotationLoading((prev) => ({ ...prev, ...doneLoading }));
				}
			}
		};

		fetchOverlays();
		}, [annotationConfigs, endDate, liveStationId, showIssueAnnotations, showMaintenanceRecordsAnnotations, showODKSubmissions, startDate, stationId, overlayReadyTick]);

	// Variable configuration for better default settings
	const allVariableConfigs = useMemo(() => {
		const configs: Record<
			string,
			{
				name: string;
				unit: string;
				color: string;
				preferredChart: 'line' | 'bar';
				aggregationMethod: string;
			}
		> = {
			pr: {
				name: 'Precipitation',
				unit: 'mm',
				color: '#0dcaf0',
				preferredChart: 'bar',
				aggregationMethod: 'sum',
			},
			te: {
				name: 'Temperature',
				unit: '°C',
				color: '#fd7e14',
				preferredChart: 'line',
				aggregationMethod: 'mean',
			},
			rh: {
				name: 'Relative Humidity',
				unit: '%',
				color: '#20c997',
				preferredChart: 'line',
				aggregationMethod: 'mean',
			},
			ap: {
				name: 'Atmospheric Pressure',
				unit: 'kPa',
				color: '#6f42c1',
				preferredChart: 'line',
				aggregationMethod: 'mean',
			},
			ws: {
				name: 'Wind Speed',
				unit: 'm/s',
				color: '#198754',
				preferredChart: 'line',
				aggregationMethod: 'mean',
			},
			wd: {
				name: 'Wind Direction',
				unit: '°',
				color: '#0d6efd',
				preferredChart: 'line',
				aggregationMethod: 'mean',
			},
			wg: { name: 'Wind Gusts', unit: 'm/s', color: '#dc3545', preferredChart: 'line', aggregationMethod: 'max' },
			ra: {
				name: 'Solar Radiation',
				unit: 'W/m²',
				color: '#ffc107',
				preferredChart: 'line',
				aggregationMethod: 'mean',
			},
			lb: {
				name: 'Battery Level',
				unit: '%',
				color: '#dc3545',
				preferredChart: 'line',
				aggregationMethod: 'mean',
			},
			lv: {
				name: 'Battery Voltage',
				unit: 'V',
				color: '#0d6efd',
				preferredChart: 'line',
				aggregationMethod: 'mean',
			},
		};
		return configs;
	}, []);

	const variableConfig = useMemo(
		() =>
			allVariableConfigs[variable] || {
				name: variable.toUpperCase(),
				unit: '',
				color: '#1f77b4',
				preferredChart: 'line',
				aggregationMethod: 'mean',
			},
		[allVariableConfigs, variable]
	);

	// Use variable-specific color if not provided
	const finalColor = color === '#1f77b4' ? variableConfig.color : color;
	const finalBackgroundColor = backgroundColor || `${finalColor}33`; // 20% opacity
	// useCallback so the reference is stable — prevents chartData useMemo from
	// rebuilding on every render just because the function identity changed.
	const normalizeHumidityValue = React.useCallback(
		(value: number | null | undefined, variableKey: string = variable): number | null | undefined => {
			if (value === null || value === undefined) return value;
			if (variableKey !== 'rh') return value;
			return value <= 1 ? value * 100 : value;
		},
		[variable]
	);

	// Reset zoom function
	const resetZoom = () => {
		if (chartRef.current) {
			const chartInstance = chartRef.current;
			if (chartInstance && chartInstance.resetZoom) {
				chartInstance.resetZoom();
			} else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
				chartInstance.chart.resetZoom();
			}
		}
		void recordUserActivity({
			actionType: 'chart_reset_zoom',
			pagePath: window.location.pathname + window.location.search,
			targetType: 'chart',
			targetLabel: `${variable}:${stationId}`,
			metadata: { stationId, variable, startDate, endDate },
		}).catch(() => {});
	};

	// Derived values needed by data-fetching effects — declared here so they are
	// available before the first useEffect that references them.
	const inferredDays = useMemo(() => {
		const metaDays = timeSeriesData?.metadata?.time_range_days;
		if (typeof metaDays === 'number') return metaDays;
		const start = moment(startDate);
		const end = moment(endDate);
		const diff = Math.max(0, end.diff(start, 'days'));
		return diff;
	}, [timeSeriesData, startDate, endDate]);

	const effectiveAggregation = useMemo(() => {
		if (aggregation !== 'auto') {
			return aggregation;
		}
		if (inferredDays > 31) return 'daily';
		return 'raw';
	}, [aggregation, inferredDays]);

	const requestMaxPoints = useMemo(() => {
		if (inferredDays > 180) return 2500;
		if (inferredDays > 31) return 4000;
		if (inferredDays > 7) return 7000;
		return 12000;
	}, [inferredDays]);

	// Fetch data effect — uses stale-while-revalidate:
	// if we already have data, keep showing it while fetching fresh data (no full-screen spinner)
	useEffect(() => {
		const fetchData = async () => {
			const hasExistingData = timeSeriesData !== null;
			if (hasExistingData) {
				// Show a subtle refresh indicator instead of hiding the chart
				setIsRefreshing(true);
			} else {
				setLoading(true);
			}
			setError(null);

			try {
					const data = await getTimeSeriesDataCached(stationId, variable, startDate, endDate, effectiveAggregation, requestMaxPoints);

					setTimeSeriesData(data);
					// Mark first successful main-series load and trigger overlay effect.
					if (!mainSeriesEverLoadedRef.current) {
						mainSeriesEverLoadedRef.current = true;
						setOverlayReadyTick((v) => v + 1);
					}
			} catch (err) {
				setError('Failed to fetch time series data');
				console.error('Error fetching time series data:', err);
			} finally {
				setLoading(false);
				setIsRefreshing(false);
			}
		};

		fetchData();
	// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [stationId, variable, startDate, endDate, effectiveAggregation, requestMaxPoints]);

	useEffect(() => {
		if (!comparisonVariables || comparisonVariables.length === 0) {
			setComparisonVariableData({});
			return;
		}
		// Prioritize first paint of the main series; fetch comparisons after it lands.
		if (!timeSeriesData) return;

		const fetchComparisonData = async () => {
			const dataMap: Record<string, TimeSeriesData | null> = {};
			const batchSize = 2;
			for (let i = 0; i < comparisonVariables.length; i += batchSize) {
				const batch = comparisonVariables.slice(i, i + batchSize);
				await Promise.all(
					batch.map(async (comparisonVariable) => {
						try {
							const data = await getTimeSeriesDataCached(
								stationId,
								comparisonVariable,
								startDate,
								endDate,
								effectiveAggregation,
								requestMaxPoints
							);
							dataMap[comparisonVariable] = data;
						} catch (err) {
							console.error(`Error fetching comparison variable ${comparisonVariable}:`, err);
							dataMap[comparisonVariable] = null;
						}
					})
				);
				setComparisonVariableData((prev) => ({ ...prev, ...dataMap }));
			}
			setComparisonVariableData(dataMap);
		};

		fetchComparisonData();
		}, [comparisonVariables, stationId, startDate, endDate, effectiveAggregation, requestMaxPoints, timeSeriesData]);

	// Fetch additional precipitation sensors data
	useEffect(() => {
		// Only fetch if we have multiple precipitation sensors
		if (!timeSeriesData?.metadata?.has_multiple_sensors || !timeSeriesData?.metadata?.additional_sensors) {
			setAdditionalSensorsData({});
			return;
		}

		const fetchAdditionalSensors = async () => {
			const dataMap: Record<string, TimeSeriesData | null> = {};
			const sensors = timeSeriesData.metadata.additional_sensors || [];

			await Promise.all(
				sensors.map(async (sensor) => {
					try {
							const data = await getTimeSeriesDataCached(
								stationId,
								sensor.sensor,
								startDate,
								endDate,
								effectiveAggregation,
								requestMaxPoints
							);
						dataMap[sensor.sensor] = data;
					} catch (err) {
						console.error(`Error fetching data for sensor ${sensor.sensor}:`, err);
						dataMap[sensor.sensor] = null;
					}
				})
			);

			setAdditionalSensorsData(dataMap);
		};

		fetchAdditionalSensors();
		}, [timeSeriesData, stationId, startDate, endDate, effectiveAggregation, requestMaxPoints]);

	// Fetch colocated stations data
	useEffect(() => {
		if (!colocatedStations || colocatedStations.length === 0) {
			setColocatedStationsData({});
			return;
		}
		// Prioritize first paint of the main series; fetch colocated stations after it lands.
		if (!timeSeriesData) return;

		const fetchColocatedData = async () => {
			const dataMap: Record<string, TimeSeriesData | null> = {};
			const batchSize = 2;
			for (let i = 0; i < colocatedStations.length; i += batchSize) {
				const batch = colocatedStations.slice(i, i + batchSize);
				await Promise.all(
					batch.map(async (station) => {
						try {
							const data = await getTimeSeriesDataCached(
								station.stationId,
								variable,
								startDate,
								endDate,
								effectiveAggregation,
								requestMaxPoints
							);
							dataMap[station.stationId] = data;
						} catch (err) {
							console.error(`Error fetching data for colocated station ${station.stationId}:`, err);
							dataMap[station.stationId] = null;
						}
					})
				);
				setColocatedStationsData((prev) => ({ ...prev, ...dataMap }));
			}

			setColocatedStationsData(dataMap);
		};

		fetchColocatedData();
		}, [colocatedStations, variable, startDate, endDate, effectiveAggregation, requestMaxPoints, timeSeriesData]);

	// Effect for fetching QC flagged data
	useEffect(() => {
		const fetchQCFlags = async () => {
			if (!showQCFlags) {
				setQCPeriods([]);
				return;
			}

			setQCLoading(true);

			try {
				const qcResponse = await getTahmoQCFlagged(stationId, variable);

				// Handle the response structure
				let qcArray = [];
				if (Array.isArray(qcResponse)) {
					qcArray = qcResponse;
				} else if (qcResponse && Array.isArray(qcResponse.data)) {
					qcArray = qcResponse.data;
				} else if (qcResponse && qcResponse.results && Array.isArray(qcResponse.results)) {
					qcArray = qcResponse.results;
				}

				setQCPeriods(qcArray);

				} catch (err) {
				console.error('Failed to fetch QC flagged data:', err);
				setQCPeriods([]);
			} finally {
				setQCLoading(false);
			}
		};

		fetchQCFlags();
	}, [stationId, variable, showQCFlags]);

	// Create data gaps plugin
	const dataGapsPlugin: Plugin<'line' | 'bar'> = useMemo(
		() => ({
			id: 'dataGapsAnnotation',
			beforeDatasetsDraw: (chart: any) => {
				if (!showDataGaps || !timeSeriesData?.data || timeSeriesData.data.length === 0) {
					return;
				}

				const ctx = chart.ctx;
				const chartArea = chart.chartArea;
				const xScale = chart.scales.x;
				const data = timeSeriesData.data;

				ctx.save();

				// Style for gap highlighting - keeping original gray colors
				ctx.fillStyle = 'rgba(128, 128, 128, 0.3)';
				ctx.strokeStyle = 'rgba(128, 128, 128, 0.8)';
				ctx.lineWidth = 2;
				ctx.setLineDash([5, 5]);

				// Sort data by timestamp to ensure proper order
				const sortedData = [...data].sort((a, b) => moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf());

				// Find gaps where there are null values
				const gaps = [];
				let gapStart = null;
				let gapStartIndex = null;

				for (let i = 0; i < sortedData.length; i++) {
					const point = sortedData[i];
					const hasValidValue = point.value !== null && point.value !== undefined && !isNaN(Number(point.value));

					if (!hasValidValue) {
						// Start of a gap
						if (gapStart === null) {
							gapStart = point.timestamp;
							gapStartIndex = i;
						}
					} else {
						// End of a gap
						if (gapStart !== null && gapStartIndex !== null) {
							// Find the actual end of the gap (the point before current valid point)
							let gapEnd = gapStart;
							if (i > 0) {
								gapEnd = sortedData[i - 1].timestamp;
							}

							gaps.push({
								start: gapStart,
								end: gapEnd,
								startIndex: gapStartIndex,
								endIndex: i - 1,
								length: i - gapStartIndex,
							});

							gapStart = null;
							gapStartIndex = null;
						}
					}
				}

				// Handle gap at the end of data
				if (gapStart !== null && gapStartIndex !== null) {
					gaps.push({
						start: gapStart,
						end: sortedData[sortedData.length - 1].timestamp,
						startIndex: gapStartIndex,
						endIndex: sortedData.length - 1,
						length: sortedData.length - gapStartIndex,
					});
				}

					// Draw each gap
				gaps.forEach((gap, index) => {
					const startTime = moment(gap.start).valueOf();
					const endTime = moment(gap.end).valueOf();

					let startX = xScale.getPixelForValue(startTime);
					let endX = xScale.getPixelForValue(endTime);

					// If it's a single point gap, make it wider for visibility
					if (startTime === endTime || Math.abs(endX - startX) < 3) {
						const centerX = (startX + endX) / 2;
						startX = centerX - 5; // 10px wide gap
						endX = centerX + 5;
					}

					// Ensure the gap is within the visible chart area
					const visibleStartX = Math.max(startX, chartArea.left);
					const visibleEndX = Math.min(endX, chartArea.right);

					// Only draw if the gap is visible
					if (visibleStartX < visibleEndX && visibleEndX > chartArea.left && visibleStartX < chartArea.right) {
						const width = visibleEndX - visibleStartX;

						// Draw background rectangle
						ctx.fillRect(visibleStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

						// Draw borders
						ctx.beginPath();
						ctx.moveTo(visibleStartX, chartArea.top);
						ctx.lineTo(visibleStartX, chartArea.bottom);
						ctx.stroke();

						ctx.beginPath();
						ctx.moveTo(visibleEndX, chartArea.top);
						ctx.lineTo(visibleEndX, chartArea.bottom);
						ctx.stroke();

						// Add label for significant gaps
						if (width > 40 && gap.length > 1) {
							ctx.save();
							ctx.font = 'bold 11px Arial';
							ctx.setLineDash([]);

							const text = 'DATA GAP';
							const textMetrics = ctx.measureText(text);
							const textWidth = textMetrics.width;
							const centerX = visibleStartX + width / 2;

							// Position labels to avoid overlap
							let labelY = chartArea.top + 30 + (index % 2) * 20;
							if (showQCFlags && qcPeriods.length > 0) labelY += 30;
							if (showIssueAnnotations && issueAnnotations.length > 0) labelY += 30;

							// Draw text background
							ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
							ctx.fillRect(centerX - textWidth / 2 - 6, labelY - 12, textWidth + 12, 20);

							// Draw text border
							ctx.strokeStyle = '#808080';
							ctx.lineWidth = 1;
							ctx.strokeRect(centerX - textWidth / 2 - 6, labelY - 12, textWidth + 12, 20);

							// Draw text
							ctx.fillStyle = '#606060';
							ctx.textAlign = 'center';
							ctx.textBaseline = 'middle';
							ctx.fillText(text, centerX, labelY - 2);

							ctx.restore();
						}
					}
				});

				ctx.restore();
			},
		}),
		[showDataGaps, timeSeriesData, showQCFlags, qcPeriods, showIssueAnnotations, issueAnnotations]
	);

	const qcFlagsPlugin: Plugin<'line' | 'bar'> = useMemo(
		() => ({
			id: 'qcFlagsAnnotation',
			// Draw QC shading behind line markers/annotations so it doesn't hide them.
			beforeDatasetsDraw: (chart) => {
				if (!showQCFlags || !qcPeriods.length) return;

				const ctx = chart.ctx;
				const chartArea = chart.chartArea;
				const xScale = chart.scales.x;

				if (!ctx || !chartArea || !xScale) return;

				// Save context state
				ctx.save();

				qcPeriods.forEach((period) => {
					if (!period.start || !period.end) return;

					// Convert QC period dates to timestamps for time scale
					const periodStartTime = moment(period.start).valueOf();
					const periodEndTime = moment(period.end).valueOf();

					// Get pixel positions using the time scale
					let startX, endX;

					try {
						startX = xScale.getPixelForValue(periodStartTime);
						endX = xScale.getPixelForValue(periodEndTime);
					} catch (error) {
						return;
					}

					// Validate pixel positions
					if (isNaN(startX) || isNaN(endX)) return;

					// Check if the period overlaps with the visible chart area
					const isVisible = startX <= chartArea.right && endX >= chartArea.left;

					if (isVisible) {
						// Calculate rectangle bounds with proper clipping
						const rectStartX = Math.max(startX, chartArea.left);
						const rectEndX = Math.min(endX, chartArea.right);
						const rectWidth = Math.max(rectEndX - rectStartX, 2); // Minimum 2px width

						// Draw background rectangle (orange/yellow for QC flags)
						ctx.fillStyle = 'rgba(255, 193, 7, 0.22)'; // Keep subtle so other overlays remain visible
						ctx.fillRect(rectStartX, chartArea.top, rectWidth, chartArea.bottom - chartArea.top);

						// Draw border
						ctx.strokeStyle = '#ffc107'; // Bootstrap warning color
						ctx.lineWidth = 2;
						ctx.setLineDash([4, 4]);
						ctx.beginPath();
						ctx.rect(rectStartX, chartArea.top, rectWidth, chartArea.bottom - chartArea.top);
						ctx.stroke();

						// Reset line dash for text
						ctx.setLineDash([]);

						// Draw label if area is wide enough (minimum 80px for text)
						if (rectWidth > 80) {
							const centerX = rectStartX + rectWidth / 2;

							ctx.font = 'bold 10px Arial';
							ctx.textAlign = 'center';
							ctx.textBaseline = 'middle';

							const labelText = `QC FLAGS`;
							const subText = `${period.count} points`;
							const textMetrics = ctx.measureText(labelText);
							const textWidth = Math.max(textMetrics.width, ctx.measureText(subText).width);
							const padding = 6;

							// Calculate label position, avoiding overlap with other annotations
							let labelY = chartArea.top + 60;
							if (showDataGaps) labelY += 40; // Offset if data gaps are shown

							// Draw label background
							ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
							ctx.fillRect(centerX - textWidth / 2 - padding, labelY - 20, textWidth + padding * 2, 32);

							// Draw label border
							ctx.strokeStyle = '#ffc107';
							ctx.lineWidth = 1;
							ctx.setLineDash([]);
							ctx.strokeRect(centerX - textWidth / 2 - padding, labelY - 20, textWidth + padding * 2, 32);

							// Draw main text
							ctx.fillStyle = '#856404'; // Darker yellow for text
							ctx.fillText(labelText, centerX, labelY - 8);

							// Draw count text
							ctx.font = '8px Arial';
							ctx.fillText(subText, centerX, labelY + 6);
						}
					}
				});

				// Restore context state
				ctx.restore();
			},
		}),
		[showQCFlags, qcPeriods, showDataGaps]
	);

	// Create issue annotations plugin
	const issueAnnotationsPlugin: Plugin<'line' | 'bar'> = useMemo(
		() => {
			return {
				id: 'issueAnnotations',
				// Draw in the final paint phase so annotation lines are never hidden.
				afterDraw: (chart: any) => {
					// Important: Check the flag first
					if (!showIssueAnnotations || !issueAnnotations || issueAnnotations.length === 0) {
						return;
					}

					const ctx = chart.ctx;
					const chartArea = chart.chartArea;
					const xScale = chart.scales.x;

					if (!ctx || !chartArea || !xScale) return;

					ctx.save();

					// Clear previous annotation positions
					(chart as any).__annotationPositions = [];

					issueAnnotations.forEach((annotation, index) => {
						// Be tolerant of backend key variations and strict parse failures.
						const rawDate =
							(annotation as any).date ||
							(annotation as any).createdAt ||
							(annotation as any).created_at ||
							(annotation as any).timestamp;
						if (!rawDate) return;
						const annotationDate = parseChartDate(rawDate);
						if (!annotationDate) return;

						let xPosition = xScale.getPixelForValue(annotationDate.valueOf() as any);
						if (!Number.isFinite(xPosition)) {
							xPosition = xScale.getPixelForValue(annotationDate.toDate() as any);
						}
						if (!Number.isFinite(xPosition)) return;

						if (xPosition >= chartArea.left && xPosition <= chartArea.right) {
							// Draw vertical line
							ctx.save();
							ctx.strokeStyle = annotation.type === 'issue' ? '#dc3545' : '#fd7e14';
						ctx.lineWidth = annotation.type === 'issue' ? 3 : 2;
						ctx.setLineDash(annotation.type === 'issue' ? [5, 5] : [5, 5]);

						ctx.beginPath();
						ctx.moveTo(xPosition, chartArea.top);
						ctx.lineTo(xPosition, chartArea.bottom);
						ctx.stroke();
						ctx.restore();

						// Draw marker at top
						ctx.fillStyle = annotation.type === 'issue' ? '#dc3545' : '#fd7e14';
						ctx.beginPath();
						ctx.arc(xPosition, chartArea.top + 10, 5, 0, 2 * Math.PI);
						ctx.fill();

						// Store annotation position for hover detection
						(chart as any).__annotationPositions.push({
							x: xPosition,
							annotation: annotation,
							index: index,
						});
					}
				});

				ctx.restore();
			},

			// Handle hover events
			beforeEvent: (chart: any, args: any) => {
				// Important: Check the flag first
				if (!showIssueAnnotations || !issueAnnotations || issueAnnotations.length === 0) {
					return true;
				}

				const event = args.event;
					if (event.type === 'mousemove') {
						const canvasPosition = getRelativePosition(event, chart);
						const chartArea = chart.chartArea;

					// Check if mouse is within chart area
					if (canvasPosition.x >= chartArea.left && canvasPosition.x <= chartArea.right && canvasPosition.y >= chartArea.top && canvasPosition.y <= chartArea.bottom) {
						const annotationPositions = (chart as any).__annotationPositions || [];
						let hoveredAnnotation = null;

						// Check if mouse is near any annotation line (within 10 pixels)
						for (const pos of annotationPositions) {
							if (Math.abs(canvasPosition.x - pos.x) <= 10) {
								hoveredAnnotation = pos.annotation;
								break;
							}
						}

						// Show custom tooltip for annotation
						if (hoveredAnnotation) {
							showAnnotationTooltip(chart, hoveredAnnotation, canvasPosition);
							chart.canvas.style.cursor = 'pointer';
						} else {
							hideAnnotationTooltip();
							chart.canvas.style.cursor = 'default';
							}
						}
					} else if (event.type === 'click') {
						const canvasPosition = getRelativePosition(event, chart);
						const chartArea = chart.chartArea;
						if (canvasPosition.x >= chartArea.left && canvasPosition.x <= chartArea.right && canvasPosition.y >= chartArea.top && canvasPosition.y <= chartArea.bottom) {
							const annotationPositions = (chart as any).__annotationPositions || [];
							for (const pos of annotationPositions) {
								if (Math.abs(canvasPosition.x - pos.x) <= 10) {
									hideAnnotationTooltip();
									setIssueModalAnnotation(pos.annotation);
									break;
								}
							}
						}
					} else if (event.type === 'mouseout') {
						hideAnnotationTooltip();
						chart.canvas.style.cursor = 'default';
				}

				return true; // Continue with normal event processing
			},
		};
		},
		[showIssueAnnotations, issueAnnotations]
	);

	// Helper functions for tooltip management (outside the plugin)
	const showAnnotationTooltip = (chart: any, annotation: IssueAnnotation, position: any) => {
		let tooltipEl = document.getElementById('annotation-tooltip');
		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.id = 'annotation-tooltip';
			tooltipEl.style.cssText = `
                position: absolute;
                background: rgba(0, 0, 0, 0.9);
                color: white;
                border-radius: 6px;
                padding: 12px;
                font-size: 12px;
                line-height: 1.4;
                max-width: 400px;
                z-index: 1000;
                pointer-events: none;
                border: 2px solid ${annotation.type === 'issue' ? '#dc3545' : '#fd7e14'};
                box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                transition: opacity 0.2s ease;
            `;
			document.body.appendChild(tooltipEl);
		}

		// Clean HTML and format content
		// const cleanMessage = annotation.message
		// 	.replace(/<[^>]*>/g, '') // Remove HTML tags
		// 	.replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
		// 	.replace(/&lt;/g, '<') // Decode HTML entities
		// 	.replace(/&gt;/g, '>')
		// 	.replace(/&amp;/g, '&')
		// 	.trim();

		const content = `
            <div style="border-bottom: 1px solid #666; padding-bottom: 8px; margin-bottom: 8px;">
                <strong style="color: ${annotation.type === 'issue' ? '#ff6b6b' : '#ffa726'};">
                    ${annotation.type.toUpperCase()}
                </strong>
                <span style="float: right; font-size: 10px; opacity: 0.8;">
                    ${moment(annotation.date).format('DD MMM YYYY HH:mm')}
                </span>
            </div>
            <div style="margin-bottom: 8px;">
                <strong>${annotation.title}</strong>
            </div>
            <div style="margin-bottom: 8px; font-size: 10px; opacity: 0.7; font-style: italic;">
                by ${annotation.author}
            </div>
            <div style="font-size: 11px; opacity: 0.9; max-height: 200px; overflow-y: auto; white-space: pre-wrap;">
                ${annotation.message}
            </div>
            ${
				annotation.priority !== 'MEDIUM'
					? `
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #666;">
                <small>Priority: <strong>${annotation.priority}</strong></small>
            </div>`
					: ''
			}
        `;

		tooltipEl.innerHTML = content;

		// Position tooltip
		const canvasRect = chart.canvas.getBoundingClientRect();
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

		let left = canvasRect.left + scrollLeft + position.x + 15;
		let top = canvasRect.top + scrollTop + position.y - 10;

		// Adjust position if tooltip would go off screen
		const tooltipRect = tooltipEl.getBoundingClientRect();
		if (left + tooltipRect.width > window.innerWidth) {
			left = canvasRect.left + scrollLeft + position.x - tooltipRect.width - 15;
		}
		if (top + tooltipRect.height > window.innerHeight) {
			top = canvasRect.top + scrollTop + position.y - tooltipRect.height + 10;
		}

		tooltipEl.style.left = left + 'px';
		tooltipEl.style.top = top + 'px';
		tooltipEl.style.opacity = '1';
	};

	const hideAnnotationTooltip = () => {
		const tooltipEl = document.getElementById('annotation-tooltip');
		if (tooltipEl) {
			tooltipEl.style.opacity = '0';
			setTimeout(() => {
				if (tooltipEl && tooltipEl.parentNode) {
					tooltipEl.parentNode.removeChild(tooltipEl);
				}
			}, 200);
		}
	};

	// Helper for QC label tooltip
	const showQCTooltip = (chart: any, hovered: any, position: any) => {
		let tooltipEl = document.getElementById('qc-label-tooltip');
		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.id = 'qc-label-tooltip';
			tooltipEl.style.cssText = `
                position: absolute;
                background: rgba(0, 0, 0, 0.9);
                color: white;
                border-radius: 6px;
                padding: 10px;
                font-size: 12px;
                line-height: 1.4;
                max-width: 420px;
                z-index: 1000;
                pointer-events: none;
                border: 2px solid #b58900;
                box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                transition: opacity 0.2s ease;
            `;
			document.body.appendChild(tooltipEl);
		}

		// Build content lines with explanations
		const lines: string[] = [];
		for (const f of hovered.flags || []) {
			const key = f.key as string;
			const val = f.value;
			const label = QC_ACRONYM_MAP[key] || key.toUpperCase();
			const explanation = QC_EXPLANATION_MAP[key];
			const sev = (typeof val === 'number' ? QC_SEVERITY_MAP[val] : undefined) || String(val);
			if (explanation && (sev === 'erroneous' || sev === 'doubtful' || sev === 'inconsistent')) {
				lines.push(`${label} (${explanation}) is ${sev}`);
			} else if (explanation && sev === 'true') {
				lines.push(`${label} (${explanation}) was triggered`);
			} else if (explanation) {
				lines.push(`${label} (${explanation}): ${sev}`);
			} else {
				lines.push(`${label}: ${sev}`);
			}
		}

		// Add timestamp/value header if available
		let header = '';
		try {
			const ts = hovered.timestamp ? moment(hovered.timestamp).format('DD MMM YYYY HH:mm') : null;
			const valStr = hovered.value !== null && hovered.value !== undefined && !isNaN(Number(hovered.value)) ? String(hovered.value) : null;
			if (ts || valStr) {
				header = `<div style="border-bottom: 1px solid #666; padding-bottom: 6px; margin-bottom: 6px;">
                    ${ts ? `<strong>${ts}</strong>` : ''}
                    ${valStr ? `<span style='float:right;'>Value: ${valStr}</span>` : ''}
                </div>`;
			}
		} catch (e) {
			/* ignore */
		}

		tooltipEl.innerHTML = `${header}${lines.map((l) => `<div style='margin: 2px 0;'>• ${l}</div>`).join('')}`;

		// Position tooltip near mouse
		const canvasRect = chart.canvas.getBoundingClientRect();
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

		let left = canvasRect.left + scrollLeft + position.x + 12;
		let top = canvasRect.top + scrollTop + position.y - 12;

		const tooltipRect = tooltipEl.getBoundingClientRect();
		if (left + tooltipRect.width > window.innerWidth) {
			left = canvasRect.left + scrollLeft + position.x - tooltipRect.width - 12;
		}
		if (top + tooltipRect.height > window.innerHeight) {
			top = canvasRect.top + scrollTop + position.y - tooltipRect.height + 12;
		}

		tooltipEl.style.left = left + 'px';
		tooltipEl.style.top = top + 'px';
		tooltipEl.style.opacity = '1';
	};

	// Create maintenance records annotations plugin
	const maintenanceAnnotationsPlugin: Plugin<'line' | 'bar'> = useMemo(
		() => ({
			id: 'maintenanceAnnotations',
			// Draw in final phase so lines are not hidden by dataset paint order.
			afterDraw: (chart: any) => {
				// Important: Check the flag first
				if (!showMaintenanceRecordsAnnotations || !maintenanceReportAnnotation || maintenanceReportAnnotation.length === 0) {
					return;
				}

				const ctx = chart.ctx;
				const chartArea = chart.chartArea;
				const xScale = chart.scales.x;

				ctx.save();

				// Clear previous annotation positions
				(chart as any).__maintenanceAnnotationPositions = [];

				maintenanceReportAnnotation.forEach((report, index) => {
					const rawDate =
						(report as any).reportDate ||
						(report as any).report_date ||
						(report as any).date ||
						(report as any).timestamp ||
						(report as any).reportTime ||
						(report as any).report_time ||
						(report as any).createdAt ||
						(report as any).created_at ||
						(report as any).updatedAt ||
						(report as any).updated_at;
					if (!rawDate) return;

					const reportDate = parseChartDate(rawDate);
					if (!reportDate) return;

					let xPosition = xScale.getPixelForValue(reportDate.valueOf() as any);
					if (!Number.isFinite(xPosition)) {
						xPosition = xScale.getPixelForValue(reportDate.toDate() as any);
					}
					if (!Number.isFinite(xPosition)) return;

					if (xPosition >= chartArea.left && xPosition <= chartArea.right) {
						// Draw vertical line (using green/teal color for maintenance)
						ctx.strokeStyle = '#20c997'; // Teal color for maintenance
						ctx.lineWidth = 2;
						ctx.setLineDash([10, 5]); // Distinct dash pattern

						ctx.beginPath();
						ctx.moveTo(xPosition, chartArea.top);
						ctx.lineTo(xPosition, chartArea.bottom);
						ctx.stroke();

						// Draw marker at top (wrench icon style)
						ctx.fillStyle = '#20c997';
						ctx.beginPath();
						ctx.arc(xPosition, chartArea.top + 10, 5, 0, 2 * Math.PI);
						ctx.fill();

						// Add small inner circle for wrench effect
						ctx.fillStyle = '#ffffff';
						ctx.beginPath();
						ctx.arc(xPosition, chartArea.top + 10, 2, 0, 2 * Math.PI);
						ctx.fill();

						// Store annotation position for hover detection
						(chart as any).__maintenanceAnnotationPositions.push({
							x: xPosition,
							report: report,
							index: index,
						});
					}
				});

				ctx.restore();
			},

			// Handle hover events
			beforeEvent: (chart: any, args: any) => {
				// Important: Check the flag first
				if (!showMaintenanceRecordsAnnotations || !maintenanceReportAnnotation || maintenanceReportAnnotation.length === 0) {
					return true;
				}

				const event = args.event;
				if (event.type === 'mousemove') {
					const canvasPosition = getRelativePosition(event, chart);
					const chartArea = chart.chartArea;

					// Check if mouse is within chart area
					if (canvasPosition.x >= chartArea.left && canvasPosition.x <= chartArea.right && canvasPosition.y >= chartArea.top && canvasPosition.y <= chartArea.bottom) {
						const maintenancePositions = (chart as any).__maintenanceAnnotationPositions || [];
						let hoveredReport = null;

						// Check if mouse is near any maintenance annotation line (within 10 pixels)
						for (const pos of maintenancePositions) {
							if (Math.abs(canvasPosition.x - pos.x) <= 10) {
								hoveredReport = pos.report;
								break;
							}
						}

						// Show custom tooltip for maintenance report
						if (hoveredReport) {
							showMaintenanceTooltip(chart, hoveredReport, canvasPosition);
							chart.canvas.style.cursor = 'pointer';
						} else {
							hideMaintenanceTooltip();
							chart.canvas.style.cursor = 'default';
							}
						}
					} else if (event.type === 'click') {
						const canvasPosition = getRelativePosition(event, chart);
						const chartArea = chart.chartArea;
						if (canvasPosition.x >= chartArea.left && canvasPosition.x <= chartArea.right && canvasPosition.y >= chartArea.top && canvasPosition.y <= chartArea.bottom) {
							const maintenancePositions = (chart as any).__maintenanceAnnotationPositions || [];
							for (const pos of maintenancePositions) {
								if (Math.abs(canvasPosition.x - pos.x) <= 10) {
									hideMaintenanceTooltip();
									setMaintenanceModalReport(pos.report);
									break;
								}
							}
						}
					} else if (event.type === 'mouseout') {
						hideMaintenanceTooltip();
						chart.canvas.style.cursor = 'default';
				}

				return true; // Continue with normal event processing
			},
		}),
		[showMaintenanceRecordsAnnotations, maintenanceReportAnnotation]
	);

	// Helper functions for maintenance tooltip management
	const showMaintenanceTooltip = (chart: any, report: IReport, position: any) => {
		let tooltipEl = document.getElementById('maintenance-tooltip');
		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.id = 'maintenance-tooltip';
			tooltipEl.style.cssText = `
                position: absolute;
                background: rgba(0, 0, 0, 0.9);
                color: white;
                border-radius: 6px;
                padding: 12px;
                font-size: 12px;
                line-height: 1.4;
                max-width: 400px;
                z-index: 1000;
                pointer-events: none;
                border: 2px solid #20c997;
                box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                transition: opacity 0.2s ease;
            `;
			document.body.appendChild(tooltipEl);
		}

		const maintenancePhotos = getMaintenancePhotos(report);

		const content = `
					<div style="border-bottom: 1px solid #666; padding-bottom: 8px; margin-bottom: 8px;">
						<strong style="color: #20c997;">
							<i class="fa fa-wrench" style="margin-right: 4px;"></i>MAINTENANCE REPORT
						</strong>
						<span style="float: right; font-size: 10px; opacity: 0.8;">
							${moment(
								(report as any).reportDate ||
								(report as any).report_date ||
								(report as any).createdAt ||
								(report as any).created_at
							).format('DD MMM YYYY HH:mm')}
						</span>
					</div>

				<div style="margin-bottom: 8px;">
					Type: <strong>${report.reportType || 'N/A'}</strong>
				</div>

				<div style="margin-bottom: 8px;">
					Report ID: <strong>${report.Id || 'N/A'}</strong>
				</div>

				<div style="margin-bottom: 8px;">
					Technician: <strong>${report.technicianDisplayname || 'N/A'}</strong>
				</div>

				<div style="margin-bottom: 8px;">
					Technician Email: <strong>${report.technicianDisplayname || 'N/A'}</strong>
				</div>

				<div style="margin-bottom: 8px;">
					Station Id: <strong>${report.stationId || 'N/A'}</strong>
				</div>

				<div style="margin-bottom: 8px;">
					Station Name: <strong>${report.stationName || 'N/A'}</strong>
				</div>

				<div style="margin-bottom: 8px;">
					Created At: <strong>${report.createdAt || 'N/A'}</strong>
				</div>

				<div style="margin-bottom: 8px;">
					Updated At: <strong>${report.updatedAt || 'N/A'}</strong>
				</div>

				${
					report.reportType === 'Datalogger Replacement'
						? `
					<div style="margin-bottom: 8px;">
						Datalogger Id: <strong>${report.selectedDataloggerId || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Datalogger Name: <strong>${report.selectedDataloggerName || 'N/A'}</strong>
					</div>
				`
						: ''
				}

				${
					report.reportType === 'Sensor Replacement'
						? `
					<div style="margin-bottom: 8px;">
						Old Sensor Id: <strong>${report.oldSensorId || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Old Sensor Name: <strong>${report.oldSensorName || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						New Sensor Id: <strong>${report.newSensorId || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						New Sensor Name: <strong>${report.newSensorName || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Port: <strong>${report.port || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Elevation: <strong>${report.elevation || 'N/A'}</strong>
					</div>
				`
						: ''
				}

				${
					report.reportType === 'Station Maintenance'
						? `
					<div style="margin-bottom: 8px;">
						Status: <strong>${report.status || 'N/A'}</strong>
					</div>
				`
						: ''
				}

				${
					report.reportType === 'Sensor Calibration'
						? `
					<div style="margin-bottom: 8px;">
						Sensor Id: <strong>${report.sensorId || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Sensor Name: <strong>${report.sensorName || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Start Date: <strong>${report.startingDate || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						End Date: <strong>${report.endingDate || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Status: <strong>${report.status || 'N/A'}</strong>
					</div>
				`
						: ''
				}

				${
					report.reportType === 'Sensor Installation'
						? `
					<div style="margin-bottom: 8px;">
						Sensor Id: <strong>${report.sensorId || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Sensor Name: <strong>${report.sensorName || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Port: <strong>${report.port || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Elevation: <strong>${report.elevation || 'N/A'}</strong>
					</div>
				`
						: ''
				}

				${
					report.reportType === 'Precipitation Calibration'
						? `
					<div style="margin-bottom: 8px;">
						Report Timezone: <strong>${report.reportTimezone || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Start Date: <strong>${report.startingDate || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						End Date: <strong>${report.endingDate || 'N/A'}</strong>
					</div>
					<div style="margin-bottom: 8px;">
						Status: <strong>${report.status || 'N/A'}</strong>
					</div>
				`
						: ''
				}

				${
					maintenancePhotos.length > 0
						? `
				<div style="margin-top: 8px; margin-bottom: 6px; font-size: 11px; opacity: 0.9; font-weight: 700;">Photos</div>
				<div class='row row-cols-4' style="margin-bottom: 8px;">
					${maintenancePhotos
						.slice(0, 6)
						.map((photo) => {
							return `<div className="col"><img class='w-100' src="${photo.src}" alt="${photo.label}" /></div>`;
						})
						.join('')}
				</div>
				`
						: ''
				}
			`;

		tooltipEl.innerHTML = content;

		// Position tooltip
		const canvasRect = chart.canvas.getBoundingClientRect();
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

		let left = canvasRect.left + scrollLeft + position.x + 15;
		let top = canvasRect.top + scrollTop + position.y - 10;

		// Adjust position if tooltip would go off screen
		const tooltipRect = tooltipEl.getBoundingClientRect();
		if (left + tooltipRect.width > window.innerWidth) {
			left = canvasRect.left + scrollLeft + position.x - tooltipRect.width - 15;
		}
		if (top + tooltipRect.height > window.innerHeight) {
			top = canvasRect.top + scrollTop + position.y - tooltipRect.height + 10;
		}

		tooltipEl.style.left = left + 'px';
		tooltipEl.style.top = top + 'px';
		tooltipEl.style.opacity = '1';
	};

	const hideMaintenanceTooltip = () => {
		const tooltipEl = document.getElementById('maintenance-tooltip');
		if (tooltipEl) {
			tooltipEl.style.opacity = '0';
			setTimeout(() => {
				if (tooltipEl && tooltipEl.parentNode) {
					tooltipEl.parentNode.removeChild(tooltipEl);
				}
			}, 200);
		}
	};

	const resolveMediaSrc = (value: any): string | null => {
		if (!value || typeof value !== 'string') return null;
		if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:') || value.startsWith('blob:')) return value;
		const normalized = value.replace(/\s+/g, '');
		// Plain attachment filenames should not be treated as image payloads.
		if (/^[\w.\-]+\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(normalized)) return null;
		// Base64 payloads are typically long and only contain base64 alphabet chars.
		if (normalized.length < 80) return null;
		if (!/^[A-Za-z0-9+/=]+$/.test(normalized)) return null;
		return `data:image/*;base64,${normalized}`;
	};

	const resolveMaintenanceMediaSrc = (value: any): string | null => {
		if (!value || typeof value !== 'string') return null;
		if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:') || value.startsWith('blob:')) return value;
		const normalized = value.replace(/\s+/g, '');
		if (/^[\w.\-/]+\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(normalized)) {
			return `${MAINTENANCE_CDN_BASE}/${normalized.replace(/^\/+/, '')}`;
		}
		if (normalized.length < 80) return null;
		if (!/^[A-Za-z0-9+/=]+$/.test(normalized)) return null;
		return `data:image/*;base64,${normalized}`;
	};

	const getSubmissionValue = (submission: any, keys: string[]): any => {
		for (const key of keys) {
			const value = submission?.[key];
			if (value !== null && value !== undefined && value !== '') return value;
		}
		return null;
	};

	const hasRenderableOdkImage = (submission: any): boolean => {
		const keys = [
			['stationPhoto', 'station_photo'],
			['solarPanel', 'solar_panel'],
			['splashGuard', 'splash_guard'],
			['rainGauge', 'rain_gauge'],
			['encroachmentImage', 'encroachment_image'],
			['datalogger'],
		];
		return keys.some((group) => {
			const value = getSubmissionValue(submission, group as string[]);
			return !!resolveMediaSrc(value);
		});
	};

	// Keep overlays lightweight; hydrate ODK images only when detail modal is opened.
	useEffect(() => {
		if (!odkModalSubmission) return;
		const submissionId = getSubmissionValue(odkModalSubmission, ['instanceId', 'instance_id']);
		if (!submissionId) return;
		if (hasRenderableOdkImage(odkModalSubmission)) return;

		let cancelled = false;
		setOdkModalHydrating(true);

		getODKSubmissions(stationId, startDate, endDate, true)
			.then((res: any) => {
				if (cancelled) return;
				const items = Array.isArray(res?.data) ? res.data : [];
				const matched = items.find((item: any) => {
					const id = getSubmissionValue(item, ['instanceId', 'instance_id']);
					return String(id) === String(submissionId);
				});
				if (matched) {
					setOdkModalSubmission((prev) => {
						if (!prev) return prev;
						const prevId = getSubmissionValue(prev, ['instanceId', 'instance_id']);
						if (String(prevId) !== String(submissionId)) return prev;
						return { ...prev, ...matched };
					});
				}
			})
			.catch(() => {
				// Ignore image hydration failures; keep modal content visible.
			})
			.finally(() => {
				if (!cancelled) setOdkModalHydrating(false);
			});

		return () => {
			cancelled = true;
		};
	}, [odkModalSubmission, stationId, startDate, endDate]);

	const getMaintenancePhotos = (report: any): Array<{ src: string; label: string }> => {
		const photos: Array<{ src: string; label: string }> = [];
		const seen = new Set<string>();
		const addPhoto = (raw: any, label: string) => {
			const src = resolveMaintenanceMediaSrc(raw);
			if (!src || seen.has(src)) return;
			seen.add(src);
			photos.push({ src, label });
		};

		const namedPhotos = [
			{ keys: ['beforeCleanImage', 'before_clean_image'], label: 'Before Cleaning' },
			{ keys: ['afterCleanImage', 'after_clean_image'], label: 'After Cleaning' },
		];
		namedPhotos.forEach(({ keys, label }) => addPhoto(getSubmissionValue(report, keys), label));

		const photoUrls = Array.isArray(report?.photo_urls) ? report.photo_urls : [];
		photoUrls.forEach((p: any, idx: number) => addPhoto(p, `Photo ${idx + 1}`));

		const photosRaw = Array.isArray(report?.photos) ? report.photos : [];
		photosRaw.forEach((p: any, idx: number) => addPhoto(p, `Photo ${idx + 1}`));

		return photos;
	};

	// Plugin to draw small QC acronym labels near flagged points (raw data) and show hover tooltips
	const qcPointLabelsPlugin: Plugin<'line' | 'bar'> = useMemo(
		() => ({
			id: 'qcPointLabels',
			afterDatasetsDraw: (chart: any) => {
				if (!showQCFlags || !chart || !chart.getDatasetMeta) return;
				const meta = chart.getDatasetMeta(0);
				const dataset = chart.data?.datasets?.[0] as any;
				if (!meta || !dataset || !Array.isArray(dataset.data)) return;
				const ctx = chart.ctx;
				const points = meta.data || [];

				// Clear and prepare hitboxes for hover detection
				(chart as any).__qcLabelPositions = [];

				ctx.save();
				ctx.font = '10px Arial';
				ctx.textAlign = 'center';
				points.forEach((pt: any, idx: number) => {
					const raw = dataset.data[idx] as any;
					const qc = raw && raw.qc ? raw.qc : null;
					if (!qc) return;
					const keys = ['rc', 'rs', 'mc', 'td', 'ti', 'ts'];
					const active: string[] = [];
					const activeLower: string[] = [];
					keys.forEach((k) => {
						const v = (qc as any)[k];
						if (v !== null && v !== undefined && v !== 1 && v !== '1') {
							active.push(k.toUpperCase());
							activeLower.push(k);
						}
					});
					if (active.length === 0) return;
					const label = active.join(',');
					const x = pt.x;
					const y = pt.y - 10; // place above point
					// Draw background
					const padding = 2;
					const textWidth = ctx.measureText(label).width;
					const boxX = x - textWidth / 2 - padding;
					const boxY = y - 10;
					const boxW = textWidth + padding * 2;
					const boxH = 14;
					ctx.fillStyle = 'rgba(255, 255, 200, 0.95)';
					ctx.strokeStyle = '#b58900';
					ctx.lineWidth = 1;
					ctx.fillRect(boxX, boxY, boxW, boxH);
					ctx.strokeRect(boxX, boxY, boxW, boxH);
					// Draw text
					ctx.fillStyle = '#6b5900';
					ctx.fillText(label, x, y);

					// Store hitbox and data for hover tooltip
					(chart as any).__qcLabelPositions.push({
						rect: { x: boxX, y: boxY, w: boxW, h: boxH },
						flags: activeLower.map((k) => ({ key: k, value: (qc as any)[k] })),
						timestamp: raw && raw.x ? raw.x : null,
						value: raw && raw.y !== undefined ? raw.y : null,
					});
				});
				ctx.restore();
			},
			beforeEvent: (chart: any, args: any) => {
				if (!showQCFlags) return true;
				const event = args.event;
				const chartArea = chart.chartArea;
				if (!chartArea) return true;

				const hide = () => hideQCTooltip();

				if (event.type === 'mousemove') {
					const pos = getRelativePosition(event, chart);
					// Is inside chart
					if (pos.x < chartArea.left || pos.x > chartArea.right || pos.y < chartArea.top || pos.y > chartArea.bottom) {
						hide();
						return true;
					}
					const positions = (chart as any).__qcLabelPositions || [];
					let hovered: any = null;
					for (const p of positions) {
						if (pos.x >= p.rect.x && pos.x <= p.rect.x + p.rect.w && pos.y >= p.rect.y && pos.y <= p.rect.y + p.rect.h) {
							hovered = p;
							break;
						}
					}
					if (hovered) {
						showQCTooltip(chart, hovered, pos);
						chart.canvas.style.cursor = 'pointer';
					} else {
						hide();
						chart.canvas.style.cursor = 'default';
					}
				} else if (event.type === 'mouseout') {
					hide();
					chart.canvas.style.cursor = 'default';
				}
				return true;
			},
		}),
		[showQCFlags]
	);

	const hideQCTooltip = () => {
		const el = document.getElementById('qc-label-tooltip');
		if (el) {
			el.style.opacity = '0';
			setTimeout(() => {
				if (el && el.parentNode) el.parentNode.removeChild(el);
			}, 200);
		}
	};

	// Chart options with tooltip customization for annotations
	// Decide time unit based on requested range so 1-week shows dates instead of hours
	// Note: inferredDays and effectiveAggregation are declared earlier (before the fetch useEffects)
	const xTimeUnit: 'hour' | 'day' | 'month' = useMemo(() => {
		if (inferredDays <= 1) return 'hour';
		if (inferredDays <= 31) return 'day';
		return 'month';
	}, [inferredDays]);

	// Generic annotation plugin factory - add this after the imports and before the component
	const createAnnotationPlugin = (
		config: AnnotationConfig,
		periods: AnnotationPeriod[],
		showAnnotation: boolean,
		otherAnnotationsCount: number = 0 // To offset labels when multiple annotations are active
	): Plugin<'line' | 'bar'> => ({
		id: `${config.type}Annotation`,
		beforeDatasetsDraw: (chart: any) => {
			if (!showAnnotation || !periods || periods.length === 0) {
				return;
			}

			const ctx = chart.ctx;
			const chartArea = chart.chartArea;
			const xScale = chart.scales.x;

			if (!ctx || !chartArea || !xScale) return;

			ctx.save();

			periods.forEach((period, index) => {
				if (!period.start || !period.end) return;

				const periodStartTime = moment(period.start).valueOf();
				const periodEndTime = moment(period.end).valueOf();

				let startX, endX;
				try {
					startX = xScale.getPixelForValue(periodStartTime);
					endX = xScale.getPixelForValue(periodEndTime);
				} catch (error) {
					console.error(`Error getting pixel positions for ${config.type}:`, error);
					return;
				}

				if (isNaN(startX) || isNaN(endX)) return;

				const isVisible = startX <= chartArea.right && endX >= chartArea.left;

				if (isVisible) {
					const rectStartX = Math.max(startX, chartArea.left);
					const rectEndX = Math.min(endX, chartArea.right);
					const rectWidth = Math.max(rectEndX - rectStartX, 2);

					// Draw background rectangle
					ctx.fillStyle = config.backgroundColor;
					ctx.fillRect(rectStartX, chartArea.top, rectWidth, chartArea.bottom - chartArea.top);

					// Draw border
					ctx.strokeStyle = config.color;
					ctx.lineWidth = 2;
					ctx.setLineDash(config.borderStyle || [5, 5]);
					ctx.beginPath();
					ctx.rect(rectStartX, chartArea.top, rectWidth, chartArea.bottom - chartArea.top);
					ctx.stroke();
					ctx.setLineDash([]);

					// Draw label if area is wide enough
					if (rectWidth > 80) {
						const centerX = rectStartX + rectWidth / 2;
						ctx.font = 'bold 10px Arial';
						ctx.textAlign = 'center';
						ctx.textBaseline = 'middle';

						const labelText = period.text || config.label;
						const textMetrics = ctx.measureText(labelText);
						const textWidth = textMetrics.width;
						const padding = 6;

						// Calculate label position with offset for multiple annotations
						let labelY = chartArea.top + 30;

						// Position based on config or default to top
						switch (config.labelPosition) {
							case 'center':
								labelY = chartArea.top + (chartArea.bottom - chartArea.top) / 2;
								break;
							case 'bottom':
								labelY = chartArea.bottom - 30;
								break;
							default: // 'top'
								labelY = chartArea.top + 30 + otherAnnotationsCount * 40;
						}

						// Draw label background
						ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
						ctx.fillRect(centerX - textWidth / 2 - padding, labelY - 12, textWidth + padding * 2, 24);

						// Draw label border
						ctx.strokeStyle = config.color;
						ctx.lineWidth = 1;
						ctx.strokeRect(centerX - textWidth / 2 - padding, labelY - 12, textWidth + padding * 2, 24);

						// Draw text
						ctx.fillStyle = config.color;
						ctx.fillText(labelText, centerX, labelY);
					}
				}
			});

			ctx.restore();
		},
	});

	// Add ODK submissions plugin (after maintenanceAnnotationsPlugin)
	const odkSubmissionsPlugin: Plugin<'line' | 'bar'> = useMemo(
		() => {
			return {
				id: 'odkSubmissions',
				afterDatasetsDraw: (chart: any) => {
					if (!showODKSubmissions || !odkSubmissions || odkSubmissions.length === 0) {
						return;
					}

				const ctx = chart.ctx;
				const chartArea = chart.chartArea;
				const xScale = chart.scales.x;

					if (!ctx || !chartArea || !xScale) return;

				ctx.save();
				(chart as any).__odkSubmissionPositions = [];

					odkSubmissions.forEach((submission, index) => {
						const rawDate =
							(submission as any).submissionDate ||
							(submission as any).submission_date ||
							(submission as any).createdAt ||
							(submission as any).created_at ||
							(submission as any).today;
						const submissionDate = parseChartDate(rawDate);
						if (!submissionDate) return;

						let xPosition = xScale.getPixelForValue(submissionDate.valueOf() as any);
						if (!Number.isFinite(xPosition)) {
							xPosition = xScale.getPixelForValue(submissionDate.toDate() as any);
						}
						if (!Number.isFinite(xPosition)) return;

						if (xPosition >= chartArea.left && xPosition <= chartArea.right) {
							// Draw vertical dashed line (teal color)
						ctx.save();
						ctx.strokeStyle = '#17a2b8';
						ctx.lineWidth = 3;
						ctx.setLineDash([5, 5]);
						ctx.beginPath();
						ctx.moveTo(xPosition, chartArea.top);
						ctx.lineTo(xPosition, chartArea.bottom);
						ctx.stroke();
						ctx.restore();

						// Draw marker at top
						ctx.fillStyle = '#17a2b8';
						ctx.beginPath();
						ctx.arc(xPosition, chartArea.top + 10, 5, 0, 2 * Math.PI);
						ctx.fill();

						// Add small inner circle
						ctx.fillStyle = '#ffffff';
						ctx.beginPath();
						ctx.arc(xPosition, chartArea.top + 10, 2, 0, 2 * Math.PI);
						ctx.fill();

						(chart as any).__odkSubmissionPositions.push({
							x: xPosition,
							submission: submission,
							index: index,
						});
					}
				});

				ctx.restore();
			},

			beforeEvent: (chart: any, args: any) => {
				if (!showODKSubmissions || !odkSubmissions || odkSubmissions.length === 0) {
					return true;
				}

				const event = args.event;
				if (event.type === 'mousemove') {
					const canvasPosition = getRelativePosition(event, chart);
					const chartArea = chart.chartArea;

					if (canvasPosition.x >= chartArea.left && canvasPosition.x <= chartArea.right && canvasPosition.y >= chartArea.top && canvasPosition.y <= chartArea.bottom) {
						const submissionPositions = (chart as any).__odkSubmissionPositions || [];
						let hoveredSubmission = null;

						for (const pos of submissionPositions) {
							if (Math.abs(canvasPosition.x - pos.x) <= 10) {
								hoveredSubmission = pos.submission;
								break;
							}
						}

						if (hoveredSubmission) {
							showODKTooltip(chart, hoveredSubmission, canvasPosition);
							chart.canvas.style.cursor = 'pointer';
						} else {
							hideODKTooltip();
							chart.canvas.style.cursor = 'default';
						}
					}
				} else if (event.type === 'click') {
					// On click, open the modal for the nearest ODK submission marker
					const canvasPosition = getRelativePosition(event, chart);
					const chartArea = chart.chartArea;
					if (canvasPosition.x >= chartArea.left && canvasPosition.x <= chartArea.right && canvasPosition.y >= chartArea.top && canvasPosition.y <= chartArea.bottom) {
						const submissionPositions = (chart as any).__odkSubmissionPositions || [];
						for (const pos of submissionPositions) {
							if (Math.abs(canvasPosition.x - pos.x) <= 10) {
								hideODKTooltip();
								setOdkModalSubmission(pos.submission);
								break;
							}
						}
					}
				} else if (event.type === 'mouseout') {
					hideODKTooltip();
					chart.canvas.style.cursor = 'default';
				}

				return true;
			},
		};
		},
		[showODKSubmissions, odkSubmissions, setOdkModalSubmission]
	);

	// Force chart remount whenever overlay visibility/data changes.
	// This avoids stale plugin state where one overlay can suppress another.
	useEffect(() => {
		setChartKey((k) => k + 1);
	}, [
		showDataGaps,
		showQCFlags,
		showIssueAnnotations,
		showMaintenanceRecordsAnnotations,
		showODKSubmissions,
		annotationConfigs?.length,
		qcPeriods.length,
		issueAnnotations.length,
		maintenanceReportAnnotation.length,
		odkSubmissions.length,
	]);

	// Combine plugins
	const allPlugins = useMemo(() => {
		const combinedPlugins = [...plugins];

		if (showDataGaps && timeSeriesData?.data) {
			combinedPlugins.push(dataGapsPlugin);
		}

		if (showQCFlags && qcPeriods.length > 0) {
			combinedPlugins.push(qcFlagsPlugin);
		}
		// Always add per-point QC labels if toggle on and we have data
		if (showQCFlags && timeSeriesData?.data?.length) {
			combinedPlugins.push(qcPointLabelsPlugin);
		}

		if (showIssueAnnotations && issueAnnotations.length > 0) {
			combinedPlugins.push(issueAnnotationsPlugin);
		}

		if (showMaintenanceRecordsAnnotations && maintenanceReportAnnotation.length > 0) {
			combinedPlugins.push(maintenanceAnnotationsPlugin);
		}

		if (showODKSubmissions && odkSubmissions.length > 0) {
			combinedPlugins.push(odkSubmissionsPlugin);
		}

		// Add generic annotation plugins
		if (annotationConfigs && annotationConfigs.length > 0) {
			let annotationOffset = 0;
			annotationConfigs.forEach((config) => {
				const raw = (annotationPeriods as any)[config.type];
				const periods = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);
				if (periods.length > 0) {
					combinedPlugins.push(createAnnotationPlugin(config, periods, true, annotationOffset));
					annotationOffset++;
				}
			});
		}

		return combinedPlugins;
	}, [
		plugins,
		showDataGaps,
		dataGapsPlugin,
		showQCFlags,
		qcFlagsPlugin,
		qcPeriods,
		showIssueAnnotations,
		issueAnnotationsPlugin,
		issueAnnotations,
		showMaintenanceRecordsAnnotations,
		maintenanceAnnotationsPlugin,
		maintenanceReportAnnotation,
		timeSeriesData,
		qcPointLabelsPlugin,
		annotationConfigs,
		annotationPeriods,
		showODKSubmissions,
		odkSubmissions,
		odkSubmissionsPlugin,
	]);

	// Chart data
	const chartData: ChartData<'line' | 'bar'> = useMemo(() => {
		// Check if we have any data at all (main or colocated)
		const hasColocatedData = colocatedStations?.some((station) => {
			const stationData = colocatedStationsData[station.stationId];
			return stationData?.data && stationData.data.length > 0;
		});

		const hasMainData = timeSeriesData?.data && timeSeriesData.data.length > 0;

		// If no data at all, return empty
		if (!hasMainData && !hasColocatedData) {
			return { labels: [], datasets: [] };
		}

		// Helper to determine if a point is flagged (exclude 1 which means correct)
		const isFlagged = (qc: any): boolean => {
			if (!qc) return false;
			const keys = ['quality', 'rc', 'rs', 'mc', 'td', 'ti', 'ts'];
			return keys.some((k) => {
				const v = (qc as any)[k];
				return v !== undefined && v !== null && v !== 1 && v !== '1';
			});
		};

		// For large datasets, reduce point rendering
		const isLargeDataset = inferredDays > 90;

		const datasets: any[] = [];

		// Check if we have multiple sensors - if so, skip the main dataset as all sensors will be shown separately
		const hasMultipleSensors = timeSeriesData?.metadata?.has_multiple_sensors &&
									timeSeriesData?.metadata?.additional_sensors &&
									timeSeriesData.metadata.additional_sensors.length > 0;

		// Main station dataset - include only if no multiple sensors
		if (hasMainData && !hasMultipleSensors) {
			// Build data objects with QC metadata for each point
			const dataObjects = timeSeriesData.data.map((p) => ({
				x: new Date(p.timestamp),
				y: normalizeHumidityValue(p.value) as any,
				qc: p,
			}));

			const dataset: any = {
				label: yAxisLabel || 'Value',
				data: dataObjects,
				borderColor: finalColor,
				backgroundColor: finalBackgroundColor,
				unit: variableConfig.unit,
				borderWidth: isLargeDataset ? 1 : 2,
				fill: chartType === 'bar' ? true : false,
				tension: chartType === 'line' ? 0.1 : undefined,
				// Wider bars for precipitation
				...(chartType === 'bar' ? { barPercentage: 1.0, categoryPercentage: 0.95 } : {}),
				// Store station ID for tooltip
				stationId: stationId,
				liveStationId: liveStationId,
				// Highlight flagged points when QC is enabled
				pointRadius: (ctx: any) => {
					if (isLargeDataset) return 0; // Don't show points for large datasets
					const raw = ctx?.raw as any;
					return chartType === 'line' && showQCFlags && raw && isFlagged(raw.qc) ? 3 : chartType === 'line' ? 1 : 0;
				},
				pointHoverRadius: (ctx: any) => {
					if (isLargeDataset) return 3;
					const raw = ctx?.raw as any;
					return chartType === 'line' && showQCFlags && raw && isFlagged(raw.qc) ? 5 : chartType === 'line' ? 4 : 0;
				},
				pointBackgroundColor: (ctx: any) => {
					const raw = ctx?.raw as any;
					return showQCFlags && raw && isFlagged(raw.qc) ? '#dc3545' : finalColor;
				},
				pointBorderColor: (ctx: any) => {
					const raw = ctx?.raw as any;
					return showQCFlags && raw && isFlagged(raw.qc) ? '#dc3545' : finalColor;
				},
			};

			datasets.push(dataset);
		}

		// Add colocated stations datasets
		colocatedStations?.forEach((station) => {
			const stationData = colocatedStationsData[station.stationId];
			if (stationData?.data) {
				const colocDataObjects = stationData.data.map((p) => ({
					x: new Date(p.timestamp),
					y: normalizeHumidityValue(p.value) as any,
					qc: p,
				}));

				const stationColor = station.color || '#17a2b8';
				const colocDataset: any = {
					label: `${station.name} (${station.stationId})`, // Include station ID in legend
					data: colocDataObjects,
					borderColor: stationColor,
					backgroundColor: chartType === 'bar' ? stationColor + '80' : stationColor + '40',
					unit: variableConfig.unit,
					borderWidth: isLargeDataset ? 1 : 2,
					fill: chartType === 'bar' ? true : false,
					tension: chartType === 'line' ? 0.1 : undefined,
					pointRadius: isLargeDataset ? 0 : chartType === 'line' ? 1 : 0,
					pointHoverRadius: isLargeDataset ? 3 : chartType === 'line' ? 4 : 0,
					// Store station ID for tooltip
					stationId: station.stationId,
					liveStationId: station.liveStationId,
					// For bar charts, set a different type to overlay properly
					...(chartType === 'bar' ? { type: 'bar' as const } : {}),
				};

				datasets.push(colocDataset);
			}
		});

		// Add additional precipitation sensors datasets
		const additionalSensors = timeSeriesData?.metadata?.additional_sensors || [];
		const sensorColors = ['#ff6384', '#36a2eb', '#ffcd56', '#4bc0c0', '#9966ff', '#ff9f40'];

		additionalSensors.forEach((sensor, index) => {
			// Check if this sensor is the primary one (matches the variable prop)
			// If so, use the already-loaded timeSeriesData instead of fetching separately
			const isPrimarySensor = sensor.sensor === variable;
			const sensorData = isPrimarySensor ? timeSeriesData : additionalSensorsData[sensor.sensor];

			// Map data or use empty array if no data available
			const sensorDataObjects = sensorData?.data
				? sensorData.data.map((p) => ({
					x: new Date(p.timestamp),
					y: normalizeHumidityValue(p.value) as any,
					qc: p,
				}))
				: [];

			const sensorColor = sensorColors[index % sensorColors.length];
			const sensorDataset: any = {
				label: sensor.name || `${yAxisLabel || variable} - ${sensor.sensor}`,
				data: sensorDataObjects,
				borderColor: sensorColor,
				backgroundColor: chartType === 'bar' ? sensorColor + '80' : sensorColor + '40',
				unit: variableConfig.unit,
				borderWidth: isLargeDataset ? 1 : 2,
				fill: chartType === 'bar' ? true : false,
				tension: chartType === 'line' ? 0.1 : undefined,
				pointRadius: isLargeDataset ? 0 : chartType === 'line' ? 1 : 0,
				pointHoverRadius: isLargeDataset ? 3 : chartType === 'line' ? 4 : 0,
				// Store sensor ID for tooltip
				sensorId: sensor.sensor,
				// For bar charts, set a different type to overlay properly
				...(chartType === 'bar' ? { type: 'bar' as const } : {}),
			};

			datasets.push(sensorDataset);
		});

		// Overlay comparison variables in the same chart
		comparisonVariables.forEach((comparisonVariable) => {
			const compData = comparisonVariableData[comparisonVariable];
			if (!compData?.data || compData.data.length === 0) return;
			const comparisonConfig = allVariableConfigs[comparisonVariable] || {
				name: comparisonVariable.toUpperCase(),
				unit: '',
				color: '#6c757d',
			};

			const compDataObjects = compData.data.map((p) => ({
				x: new Date(p.timestamp),
				y: normalizeHumidityValue(p.value, comparisonVariable) as any,
				qc: p,
			}));

			datasets.push({
				label: `${comparisonConfig.name} (${comparisonConfig.unit})`,
				data: compDataObjects,
				borderColor: comparisonConfig.color,
				backgroundColor: `${comparisonConfig.color}33`,
				unit: comparisonConfig.unit,
				borderWidth: isLargeDataset ? 1 : 2,
				fill: false,
				tension: 0.1,
				pointRadius: isLargeDataset ? 0 : 1,
				pointHoverRadius: isLargeDataset ? 3 : 4,
				stationId: stationId,
				liveStationId: liveStationId,
				yAxisID: `y_${comparisonVariable}`,
				// Always render comparison variables as lines, even when the primary chart is a bar chart
				type: 'line' as const,
			});
		});

		const result: any = {
			// No need for separate labels; time scale reads x from data objects
			datasets,
		};

		return result;
	}, [
		timeSeriesData,
		colocatedStations,
		colocatedStationsData,
		additionalSensorsData,
		comparisonVariables,
		comparisonVariableData,
		allVariableConfigs,
		finalColor,
		finalBackgroundColor,
		chartType,
		yAxisLabel,
			showQCFlags,
		inferredDays,
		variable,
		variableConfig.unit,
		stationId,
		liveStationId,
	]);

	// Add tooltip helper functions (after hideMaintenanceTooltip)
	const showODKTooltip = (chart: any, submission: ODKSubmission, position: any) => {
		let tooltipEl = document.getElementById('odk-tooltip');
		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.id = 'odk-tooltip';
			tooltipEl.style.cssText = `
            position: absolute;
            background: rgba(0, 0, 0, 0.9);
            color: white;
            border-radius: 6px;
            padding: 12px;
            font-size: 12px;
            line-height: 1.4;
            max-width: 700px;
            z-index: 1000;
            pointer-events: none;
            border: 2px solid #17a2b8;
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
            transition: opacity 0.2s ease;
        `;
			document.body.appendChild(tooltipEl);
		}

		const locationName = getSubmissionValue(submission, ['locationName', 'location_name']) || '';
		const stationIdLabel = getSubmissionValue(submission, ['stationId', 'station_id']) || '';
		const hostName = getSubmissionValue(submission, ['hostName', 'host_name']) || '';
		const comment = getSubmissionValue(submission, ['comment']) || '';
		const encroachment = getSubmissionValue(submission, ['encroachment']) || '';
		const explainEncroachment = getSubmissionValue(submission, ['explainEncroachment', 'explain_encroachment']) || '';
		const today = getSubmissionValue(submission, ['today']) || '';
		const start = getSubmissionValue(submission, ['start']) || '';
		const end = getSubmissionValue(submission, ['end']) || '';
		const stationPhotoSrc = resolveMediaSrc(getSubmissionValue(submission, ['stationPhoto', 'station_photo']));
		const solarPanelSrc = resolveMediaSrc(getSubmissionValue(submission, ['solarPanel', 'solar_panel']));
		const splashGuardSrc = resolveMediaSrc(getSubmissionValue(submission, ['splashGuard', 'splash_guard']));
		const rainGaugeSrc = resolveMediaSrc(getSubmissionValue(submission, ['rainGauge', 'rain_gauge']));
		const encroachmentImageSrc = resolveMediaSrc(getSubmissionValue(submission, ['encroachmentImage', 'encroachment_image']));
		const dataloggerSrc = resolveMediaSrc(getSubmissionValue(submission, ['datalogger']));

		const content = `
			<div style="border-bottom: 1px solid #666; padding-bottom: 8px; margin-bottom: 8px;">
				<strong style="color: #17a2b8;">
					<i class="fa fa-file-text" style="margin-right: 4px;"></i>FIELD SUBMISSION
				</strong>
				<span style="float: right; font-size: 10px; opacity: 0.8;">
					${moment(submission.submissionDate).format('DD MMM YYYY HH:mm')}
				</span>
			</div>

			<div style="margin-bottom: 8px; font-size: 10px; opacity: 0.7; font-style: italic;">
			Location Name: ${locationName}</div>

			<div style="margin-bottom: 8px; font-size: 10px; opacity: 0.7; font-style: italic;">
			Station ID: ${stationIdLabel}</div>

			<div style="margin-bottom: 8px; font-size: 10px; opacity: 0.7; font-style: italic;">
			Host Name: ${hostName}</div>

			<div style="margin-bottom: 8px; font-size: 10px; opacity: 0.7; font-style: italic;">
			Comment: ${comment}</div>

			<div style="margin-bottom: 8px; font-size: 10px; opacity: 0.7; font-style: italic;">
			Encroachment: ${encroachment}</div>

			<div style="margin-bottom: 8px; font-size: 10px; opacity: 0.7; font-style: italic;">
			Encroachment Explanation: ${explainEncroachment}</div>

			<div style="margin-bottom: 8px; font-size: 10px; opacity: 0.7; font-style: italic;">
			Date Recorded: ${today}</div>

			<div style="margin-bottom: 8px; font-size: 10px; opacity: 0.7; font-style: italic;">
			Start Date: ${start}</div>

			<div style="margin-bottom: 8px; font-size: 10px; opacity: 0.7; font-style: italic;">
			End Date: ${end}</div>

			<div class="row row-cols-6">
				${
					stationPhotoSrc
						? `<div className="col">
						<img class="w-100" src="${stationPhotoSrc}" alt='station photo' />
						<span>Station Photo</span>
					</div>`
						: ''
				}
				
				${
					solarPanelSrc
						? `<div className="col">
						<img class="w-100" src="${solarPanelSrc}" alt='Solar Panel' />
						<span>Solar Panel</span>
					</div>`
						: ''
				}
				${
					splashGuardSrc
						? `<div className="col">
						<img class="w-100" src="${splashGuardSrc}" alt='Splash Guard' />
						<span>Splash Guard</span>
					</div>`
						: ''
				}
				${
					rainGaugeSrc
						? `<div className="col">
						<img class="w-100" src="${rainGaugeSrc}" alt='Rain Gauge' />
						<span>Rain Gauge</span>
					</div>`
						: ''
				}
				${
					encroachmentImageSrc
						? `<div className="col">
						<img class="w-100" src="${encroachmentImageSrc}" alt='Encroachment Image' />
						<span>Encroachment Image</span>
					</div>`
						: ''
				}
				${
					dataloggerSrc
						? `<div className="col">
						<img class="w-100" src="${dataloggerSrc}" alt='Datalogger' />
						<span>Datalogger</span>
					</div>`
						: ''
				}
			</div>
		`;

		tooltipEl.innerHTML = content;

		const canvasRect = chart.canvas.getBoundingClientRect();
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

		let left = canvasRect.left + scrollLeft + position.x + 15;
		let top = canvasRect.top + scrollTop + position.y - 10;

		const tooltipRect = tooltipEl.getBoundingClientRect();
		if (left + tooltipRect.width > window.innerWidth) {
			left = canvasRect.left + scrollLeft + position.x - tooltipRect.width - 15;
		}
		if (top + tooltipRect.height > window.innerHeight) {
			top = canvasRect.top + scrollTop + position.y - tooltipRect.height + 10;
		}

		tooltipEl.style.left = left + 'px';
		tooltipEl.style.top = top + 'px';
		tooltipEl.style.opacity = '1';
	};

	const hideODKTooltip = () => {
		const tooltipEl = document.getElementById('odk-tooltip');
		if (tooltipEl) {
			tooltipEl.style.opacity = '0';
			setTimeout(() => {
				if (tooltipEl && tooltipEl.parentNode) {
					tooltipEl.parentNode.removeChild(tooltipEl);
				}
			}, 200);
		}
	};

	const broadcastSyncEvent = (chartInstance: any) => {
		if (!syncEnabled || !syncKey) return;
		try {
			const xScale = chartInstance?.scales?.x;
			const min = xScale?.min;
			const max = xScale?.max;
			if (typeof min === 'number' && typeof max === 'number') {
				setLastRange(syncKey, { min, max });
				const event = new CustomEvent(SYNC_EVENT, {
					detail: { syncKey, sourceId: syncIdRef.current, min, max },
				});
				window.dispatchEvent(event);
			}
		} catch (e) {
			console.error('Broadcast sync failed:', e);
		}
	};

	// Compute whether legend should be displayed
	const shouldDisplayLegend = useMemo(() => {
		return Boolean((colocatedStations && colocatedStations.length > 0) ||
			(comparisonVariables && comparisonVariables.length > 0) ||
			(timeSeriesData?.metadata?.has_multiple_sensors &&
			 timeSeriesData?.metadata?.additional_sensors &&
			 timeSeriesData.metadata.additional_sensors.length > 0));
	}, [colocatedStations, comparisonVariables, timeSeriesData?.metadata?.has_multiple_sensors, timeSeriesData?.metadata?.additional_sensors]);

	const chartOptions: ChartOptions<'line' | 'bar'> = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			animation: false,
			normalized: true,
			scales: {
				x: {
					type: 'time',
					time: {
						unit: xTimeUnit,
						displayFormats: {
							hour: 'HH:mm',
							day: 'DD MMM',
							week: 'DD MMM',
							month: 'DD MMM YY',
							year: 'DD MMM YYYY',
						},
						tooltipFormat: 'DD MMM YYYY HH:mm',
					},
					ticks: {
						autoSkip: true,
						autoSkipPadding: 50,
						maxRotation: 0,
						maxTicksLimit: inferredDays > 90 ? 12 : xTimeUnit === 'day' ? 15 : undefined,
						source: 'auto',
					},
					title: {
						display: true,
						text: 'Date',
					},
				},
				y: {
					position: 'left' as const,
					title: {
						display: true,
						text: yAxisLabel || 'Value',
					},
					beginAtZero: variable === 'pr', // Start from 0 for precipitation
				},
				// Dynamically add a right-side y-axis for each comparison variable
				...Object.fromEntries(
					(comparisonVariables || []).map((compVar: string) => {
						const cfg = allVariableConfigs[compVar] || { name: compVar.toUpperCase(), unit: '', color: '#6c757d' };
						return [
							`y_${compVar}`,
							{
								type: 'linear' as const,
								position: 'right' as const,
								title: {
									display: true,
									text: cfg.unit ? `${cfg.name} (${cfg.unit})` : cfg.name,
									color: cfg.color,
								},
								ticks: {
									color: cfg.color,
								},
								grid: {
									drawOnChartArea: false, // Don't draw grid lines for secondary axes
								},
								beginAtZero: compVar === 'pr',
							},
						];
					})
				),
			},
			// Optimize interaction for large datasets
			interaction: {
				mode: 'index',
				axis: 'x',
				intersect: false,
			},
			// Reduce point rendering for large datasets
			elements: {
				point: {
					radius: inferredDays > 90 ? 0 : chartType === 'line' ? 1 : 0,
					hitRadius: inferredDays > 90 ? 5 : 3,
					hoverRadius: inferredDays > 90 ? 3 : 4,
				},
				line: {
					borderWidth: inferredDays > 90 ? 1 : 2,
				},
			},
			plugins: {
				legend: {
					display: shouldDisplayLegend,
					position: 'top' as const,
				},
				zoom: {
					zoom: {
						wheel: { enabled: enableZoom },
						pinch: { enabled: enableZoom },
						// drag: { enabled: enableZoom },
						mode: 'x',
						onZoomComplete: ({ chart }: { chart: ChartJS }) => {
							// Always cache the zoom range, even without sync
							const xScale = chart.scales?.x;
							if (xScale && typeof xScale.min === 'number' && typeof xScale.max === 'number') {
								setLastRange(syncKey || 'local', { min: xScale.min, max: xScale.max });
								void recordUserActivity({
									actionType: 'chart_zoom',
									pagePath: window.location.pathname + window.location.search,
									targetType: 'chart',
									targetLabel: `${variable}:${stationId}`,
									metadata: {
										stationId,
										variable,
										min: xScale.min,
										max: xScale.max,
									},
								}).catch(() => {});
							}
							if (syncEnabled && syncKey) broadcastSyncEvent(chart);
						},
					},
					pan: {
						enabled: enableZoom,
						mode: 'x',
						onPanComplete: ({ chart }: { chart: ChartJS }) => {
							// Always cache the zoom range, even without sync
							const xScale = chart.scales?.x;
							if (xScale && typeof xScale.min === 'number' && typeof xScale.max === 'number') {
								setLastRange(syncKey || 'local', { min: xScale.min, max: xScale.max });
								void recordUserActivity({
									actionType: 'chart_pan',
									pagePath: window.location.pathname + window.location.search,
									targetType: 'chart',
									targetLabel: `${variable}:${stationId}`,
									metadata: {
										stationId,
										variable,
										min: xScale.min,
										max: xScale.max,
									},
								}).catch(() => {});
							}
							if (syncEnabled && syncKey) broadcastSyncEvent(chart);
						},
					},
					limits: {
						x: {
							min: 'original',
							max: 'original',
						},
					},
				},
				tooltip: {
					enabled: false, // Disable default tooltip
					external: (context) => {
						// Get the tooltip element or create it
						let tooltipEl = document.getElementById('chartjs-tooltip');
						if (!tooltipEl) {
							tooltipEl = document.createElement('div');
							tooltipEl.id = 'chartjs-tooltip';
							tooltipEl.innerHTML = '<table></table>';
							tooltipEl.style.cssText = `
                            opacity: 0;
                            position: absolute;
                            background: rgba(0, 0, 0, 0.8);
                            color: white;
                            border-radius: 3px;
                            -webkit-transition: all .1s ease;
                            transition: all .1s ease;
                            pointer-events: none;
                            transform: translate(-50%, 0);
                            padding: 10px;
                            max-width: 400px;
                            z-index: 1000;
                            font-size: 12px;
                            line-height: 1.4;
                        `;
							document.body.appendChild(tooltipEl);
						}

						const tooltipModel = context.tooltip;

						// Hide if no tooltip
						if (tooltipModel.opacity === 0) {
							tooltipEl.style.opacity = '0';
							return;
						}

						// Set content
						if (tooltipModel.body) {
							const titleLines = tooltipModel.title || [];
							const dataPoints = tooltipModel.dataPoints || [];
							const chart = context.chart;

							let innerHtml = '<thead>';
							titleLines.forEach((title) => {
								innerHtml += '<tr><th style="text-align: left; padding-bottom: 5px;">' + title + '</th></tr>';
							});
							innerHtml += '</thead><tbody>';

							// Get all datasets from the chart
							const allDatasets = chart?.data?.datasets || [];

							// If we have colocated stations but main station has no data, we need to show main station as "No data"
							if (allDatasets.length > 0) {
								// Check if main station data is present in tooltip
								const mainDatasetInTooltip = dataPoints.find((dp: any) =>
									dp.dataset.stationId === stationId || dp.datasetIndex === 0
								);

								// If main station is not in tooltip but we have colocated data, show main as "No data"
								if (!mainDatasetInTooltip && dataPoints.length > 0 && allDatasets.length > 0) {
									const mainDataset = allDatasets[0] as any;
									const label = mainDataset.label || 'Value';
									const stationIdDisplay = mainDataset.stationId || mainDataset.liveStationId || '';
									const stationInfo = stationIdDisplay ? ` <span style="color: #888; font-size: 10px;">(${stationIdDisplay})</span>` : '';
									innerHtml += `<tr><td style="text-align: left; padding: 2px 0;">${label}${stationInfo}: <span style="color: #999; font-style: italic;">No data</span></td></tr>`;
								}
							}

							// Display each dataset with station ID
							dataPoints.forEach((dataPoint: any) => {
								const dataset = dataPoint.dataset;
								const value = dataPoint.parsed?.y;
								const label = dataset.label || 'Value';
								const stationIdDisplay = dataset.stationId || dataset.liveStationId || '';

								// Format value or show "No data" / "Null"
								let valueDisplay: string;
								if (value === null || value === undefined) {
									valueDisplay = '<span style="color: #999; font-style: italic;">No data</span>';
								} else {
									const unit = dataset.unit || '';
									const suffix = unit === '%' ? '%' : unit ? ` ${unit}` : '';
									valueDisplay = `<strong>${value.toFixed(2)}${suffix}</strong>`;
								}

								// Display with station ID
								const stationInfo = stationIdDisplay ? ` <span style="color: #888; font-size: 10px;">(${stationIdDisplay})</span>` : '';
								innerHtml += `<tr><td style="text-align: left; padding: 2px 0;">${label}${stationInfo}: ${valueDisplay}</td></tr>`;
							});

							// Add QC flags for this point if enabled and present
							if (showQCFlags) {
								const dp = tooltipModel.dataPoints && tooltipModel.dataPoints[0];
								const raw: any = dp && (dp.raw as any);
								const qc = raw && raw.qc ? raw.qc : null;
								if (qc) {
									const keys = ['quality', 'rc', 'rs', 'mc', 'td', 'ti', 'ts'];
									const items: string[] = [];
									keys.forEach((k) => {
										const v = (qc as any)[k];
										if (v !== null && v !== undefined && v !== 1 && v !== '1') {
											let label = QC_ACRONYM_MAP[k] || k.toUpperCase();
											let sev = (typeof v === 'number' ? QC_SEVERITY_MAP[v] : undefined) || String(v);
											items.push(`${k.toUpperCase()}: ${sev}`);
										}
									});
									if (items.length > 0) {
										innerHtml += '<tr><td style="padding-top: 10px; border-top: 1px solid #666;"><strong>--- QC Flags ---</strong></td></tr>';
										items.forEach((it) => {
											innerHtml += `<tr><td style="padding: 2px 0;">${it}</td></tr>`;
										});
									}
								}
							}

							// Add issue annotations if any
							if (showIssueAnnotations && issueAnnotations.length > 0) {
								const tooltipItem = tooltipModel.dataPoints?.[0];
								if (tooltipItem) {
									let timestamp: moment.Moment | null = null;

									if (tooltipItem.parsed && typeof tooltipItem.parsed.x === 'number') {
										timestamp = moment(tooltipItem.parsed.x);
									} else if (tooltipItem.label) {
										timestamp = moment(tooltipItem.label);
									}

									if (timestamp) {
										const relevantAnnotations = issueAnnotations.filter((annotation) => {
											const annotationTime = moment(annotation.date);
											const diffHours = Math.abs(annotationTime.diff(timestamp!, 'hours', true));
											return diffHours <= 24;
										});

										if (relevantAnnotations.length > 0) {
											innerHtml += '<tr><td style="padding-top: 10px; border-top: 1px solid #666;"><strong>--- Issues/Comments ---</strong></td></tr>';
											relevantAnnotations.forEach((annotation) => {
												innerHtml += `<tr><td style="padding: 5px 0;"><strong>${annotation.type.toUpperCase()}:</strong> ${annotation.title}</td></tr>`;
												if (annotation.description) {
													// Render HTML content directly
													innerHtml += `<tr><td style="padding: 2px 0; max-width: 350px; word-wrap: break-word;">${annotation.description}</td></tr>`;
												}
												if (annotation.message) {
													// Render HTML content directly
													innerHtml += `<tr><td style="padding: 2px 0; max-width: 350px; word-wrap: break-word;">${annotation.message}</td></tr>`;
												}
											});
										}
									}
								}
							}

							innerHtml += '</tbody>';
							const tableRoot = tooltipEl.querySelector('table');
							if (tableRoot) {
								tableRoot.innerHTML = innerHtml;
							}
						}

						// Position tooltip
						const position = context.chart.canvas.getBoundingClientRect();
						tooltipEl.style.opacity = '1';
						tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
						tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
					},
				},
			},
		}),
		[enableZoom, syncEnabled, yAxisLabel, variable, showIssueAnnotations, showQCFlags, inferredDays, stationId, syncKey, shouldDisplayLegend, comparisonVariables, allVariableConfigs, xTimeUnit]
	);

	// Create separate data objects for each chart type to fix TypeScript issues
	const barChartData: ChartData<'bar'> = useMemo(() => chartData as ChartData<'bar'>, [chartData]);
	const lineChartData: ChartData<'line'> = useMemo(() => chartData as ChartData<'line'>, [chartData]);

	const barChartOptions: ChartOptions<'bar'> = useMemo(() => chartOptions as ChartOptions<'bar'>, [chartOptions]);
	const lineChartOptions: ChartOptions<'line'> = useMemo(() => chartOptions as ChartOptions<'line'>, [chartOptions]);

	const ChartComponent =
		chartType === 'bar' ? (
			<Bar key={chartKey} ref={chartRef} data={barChartData} options={barChartOptions} plugins={allPlugins} />
		) : (
			<Line key={chartKey} ref={chartRef} data={lineChartData} options={lineChartOptions} plugins={allPlugins} />
		);

	// Loading state
	if (loading) {
		return (
			<div className='card'>
				<div className='card-body'>
					<div className='d-flex justify-content-center align-items-center' style={{ height: `${height}px` }}>
						<div className='text-muted'>
							<i className='fa fa-spinner fa-spin fa-3x mb-3'></i>
							<p>Loading {variableConfig.name.toLowerCase()} data...</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className='card'>
				<div className='card-body'>
					<div className='alert alert-danger' role='alert'>
						<i className='fa fa-exclamation-triangle me-2'></i>
						{error}
					</div>
				</div>
			</div>
		);
	}

	// No data state - check both main and colocated stations
	const hasMainData = timeSeriesData?.data && timeSeriesData.data.length > 0;
	const hasColocatedData = colocatedStations?.some((station) => {
		const stationData = colocatedStationsData[station.stationId];
		return stationData?.data && stationData.data.length > 0;
	});

	if (!hasMainData && !hasColocatedData) {
		return (
			<div className='card'>
				<div className='card-body'>
					<div
						className='text-center text-muted'
						style={{
							height: `${height}px`,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}>
						<div>
							<i className='fa fa-chart-line fa-3x mb-3'></i>
							<p>No {variableConfig.name.toLowerCase()} data available for the selected period</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
		<div className='card'>
			{title && (
				<div className='card-header d-flex justify-content-between align-items-center'>
					<h5 className='card-title mb-0'>{title}</h5>
					{enableZoom && !loading && timeSeriesData && (
						<button className='btn btn-sm btn-outline-secondary' onClick={resetZoom}>
							<i className='fa fa-search-minus me-1'></i>
							Reset Zoom
						</button>
					)}
				</div>
			)}
			<div className='card-body'>
				{/* Performance warning for large datasets */}
				{!loading && timeSeriesData && timeSeriesData.metadata.count > 10000 && aggregation === 'raw' && (
					<div className='alert alert-warning alert-dismissible fade show' role='alert'>
						<i className='fa fa-exclamation-triangle me-2'></i>
						<strong>Large Dataset Warning:</strong> You're viewing {timeSeriesData.metadata.count.toLocaleString()} raw data points. Consider using <strong>Daily Aggregation</strong> for
						better performance.
						<button type='button' className='btn-close' data-bs-dismiss='alert' aria-label='Close'></button>
					</div>
				)}
				{/* Subtle top-bar refresh indicator for stale-while-revalidate */}
				{isRefreshing && (
					<div style={{
						height: '3px',
						background: 'linear-gradient(90deg, #0d6efd 0%, #6ea8fe 50%, #0d6efd 100%)',
						backgroundSize: '200% 100%',
						animation: 'chartRefreshBar 1.4s ease infinite',
						borderRadius: '2px',
						marginBottom: '6px',
					}} />
				)}
				{loading ? (
					<div style={{ height: `${height}px` }}>
						{/* Skeleton loader — mirrors the chart layout */}
						<div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '8px', padding: '8px 0' }}>
							{/* Y-axis + chart area */}
							<div style={{ display: 'flex', flex: 1, gap: '8px' }}>
								{/* Y-axis skeleton */}
								<div style={{ width: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingBottom: '24px' }}>
									{[...Array(5)].map((_, i) => (
										<div key={i} style={{ height: '10px', background: '#e9ecef', borderRadius: '4px', animation: 'skeletonPulse 1.5s ease-in-out infinite', animationDelay: `${i * 0.1}s` }} />
									))}
								</div>
								{/* Chart area skeleton */}
								<div style={{ flex: 1, position: 'relative', overflow: 'hidden', borderRadius: '4px' }}>
									<div style={{ position: 'absolute', inset: 0, background: '#f8f9fa' }} />
									{/* Horizontal grid lines */}
									{[...Array(5)].map((_, i) => (
										<div key={i} style={{
											position: 'absolute', left: 0, right: 0,
											top: `${(i + 1) * 16.67}%`,
											height: '1px', background: '#dee2e6',
										}} />
									))}
									{/* Shimmer overlay */}
									<div style={{
										position: 'absolute', inset: 0,
										background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.7) 50%, transparent 100%)',
										backgroundSize: '200% 100%',
										animation: 'skeletonShimmer 1.5s ease-in-out infinite',
									}} />
								</div>
							</div>
						</div>
						{/* X-axis skeleton */}
						<div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '48px', paddingTop: '4px' }}>
							{[...Array(6)].map((_, i) => (
								<div key={i} style={{ width: '48px', height: '10px', background: '#e9ecef', borderRadius: '4px', animation: 'skeletonPulse 1.5s ease-in-out infinite', animationDelay: `${i * 0.08}s` }} />
							))}
						</div>
					</div>
				) : error ? (
					<div className='alert alert-danger' role='alert'>
						<i className='fa fa-exclamation-triangle me-2'></i>
						{error}
					</div>
				) : (
					<div>
						<div style={{ height: `${height}px` }}>{ChartComponent}</div>
						{showDataInfo && timeSeriesData && (
							<div className='mt-3'>
								<small className='text-muted d-block'>
									<i className='fa fa-info-circle me-1'></i>
									{timeSeriesData.metadata.count.toLocaleString()} data points
									{timeSeriesData.metadata.count < timeSeriesData.metadata.total_count && (
										<span> ({((timeSeriesData.metadata.count / timeSeriesData.metadata.total_count) * 100).toFixed(1)}% coverage)</span>
									)}
									{timeSeriesData.metadata.aggregation && timeSeriesData.metadata.aggregation !== 'raw' && (
										<span className='ms-3'>
											<i className='fa fa-compress me-1'></i>
											Aggregation: {timeSeriesData.metadata.aggregation}
										</span>
									)}
									{timeSeriesData.metadata.time_range_days && (
										<span className='ms-3'>
											<i className='fa fa-calendar me-1'></i>
											{timeSeriesData.metadata.time_range_days} days
										</span>
									)}
									{showDataGaps &&
										timeSeriesData?.data &&
										(() => {
											const nullCount = timeSeriesData.data.filter((p) => p.value === null || p.value === undefined).length;
											return nullCount > 0 ? (
												<span className='ms-3 text-secondary'>
													<i className='fa fa-pause me-1'></i>
													{nullCount} missing data point{nullCount !== 1 ? 's' : ''}
												</span>
											) : null;
										})()}
									{showQCFlags && (
										<span className='ms-3'>
											{qcLoading ? (
												<span className='text-info'>
													<i className='fa fa-spinner fa-spin me-1'></i>
													Loading QC flags...
												</span>
											) : qcPeriods.length > 0 ? (
												<span className='text-warning'>
													<i className='fa fa-flag me-1'></i>
													TAHMO QC: {qcPeriods.reduce((sum, period) => sum + period.count, 0)} flagged points
												</span>
											) : (
												<span className='text-success'>
													<i className='fa fa-check me-1'></i>
													No QC flags found
												</span>
											)}
										</span>
									)}
									{showIssueAnnotations && (
										<span className='ms-3'>
											{issueAnnotationsLoading ? (
												<span className='text-info'>
													<i className='fa fa-spinner fa-spin me-1'></i>
													Loading issues...
												</span>
											) : issueAnnotations.length > 0 ? (
												<span className='text-danger'>
													<i className='fa fa-exclamation-triangle me-1'></i>
													{issueAnnotations.filter((a) => a.type === 'issue').length} issues, {issueAnnotations.filter((a) => a.type === 'comment').length} comments
												</span>
											) : (
												<span className='text-success'>
													<i className='fa fa-check me-1'></i>
													No issues found
												</span>
											)}
										</span>
									)}
									{showODKSubmissions && (
										<span className='ms-3'>
											{odkSubmissionsLoading ? (
												<span className='text-info'>
													<i className='fa fa-spinner fa-spin me-1'></i>
													Loading field data...
												</span>
											) : odkSubmissions.length > 0 ? (
												<span className='text-info'>
													<i className='fa fa-file-text me-1'></i>
													{odkSubmissions.length} field submission{odkSubmissions.length !== 1 ? 's' : ''}
												</span>
											) : (
												<span className='text-muted'>
													<i className='fa fa-check me-1'></i>
													No field submissions
												</span>
											)}
										</span>
									)}
									{showMaintenanceRecordsAnnotations && (
										<span className='ms-3'>
											{maintenanceReportAnnotationLoading ? (
												<span className='text-info'>
													<i className='fa fa-spinner fa-spin me-1'></i>
													Loading maintenance records...
												</span>
											) : maintenanceReportAnnotation.length > 0 ? (
												<span className='text-danger'>
													<i className='fa fa-exclamation-triangle me-1'></i>
													{maintenanceReportAnnotation.length} maintenance report{maintenanceReportAnnotation.length !== 1 ? 's' : ''}
												</span>
											) : (
												<span className='text-success'>
													<i className='fa fa-check me-1'></i>
													No Maintenance Reports Found
												</span>
											)}
										</span>
									)}
									{annotationConfigs &&
										annotationConfigs.length > 0 &&
										annotationConfigs.map((config) => {
											const raw = (annotationPeriods as any)[config.type];
											const periods = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);
											const loading = annotationLoading[config.type];

											return (
												<span key={config.type} className='ms-3'>
													{loading ? (
														<span className='text-info'>
															<i className='fa fa-spinner fa-spin me-1'></i>
															Loading {config.label}...
														</span>
													) : periods.length > 0 ? (
														<span style={{ color: config.color }}>
															<i className='fa fa-exclamation-triangle me-1'></i>
															{periods.length} {config.label} period{periods.length !== 1 ? 's' : ''}
														</span>
													) : (
														<span className='text-muted'>
															<i className='fa fa-check me-1'></i>
															No {config.label}
														</span>
													)}
												</span>
											);
										})}
									{enableZoom && <br />}
									{enableZoom && (
										<span>
											<strong>Tip:</strong> Use mouse wheel to zoom, drag to pan, or click "Reset Zoom" to return to original view
										</span>
									)}
								</small>
							</div>
						)}
					</div>
				)}
			</div>
		</div>

			{/* Issue/Comment Detail Modal */}
			{issueModalAnnotation && (
				<div
					style={{
						position: 'fixed', inset: 0, zIndex: 2000,
						background: 'rgba(0,0,0,0.55)',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						padding: '16px',
					}}
					onClick={() => setIssueModalAnnotation(null)}
				>
					<div
						style={{
							background: '#fff', borderRadius: '10px', maxWidth: '780px', width: '100%',
							maxHeight: '90vh', overflowY: 'auto',
							boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
							border: `2px solid ${issueModalAnnotation.type === 'issue' ? '#dc3545' : '#fd7e14'}`,
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #dee2e6', background: issueModalAnnotation.type === 'issue' ? '#dc3545' : '#fd7e14', borderRadius: '8px 8px 0 0' }}>
							<strong style={{ color: '#fff', fontSize: '16px' }}>
								{issueModalAnnotation.type === 'issue' ? 'Issue' : 'Comment'}
							</strong>
							<button
								onClick={() => setIssueModalAnnotation(null)}
								style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', color: '#fff', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
								aria-label='Close'
							>
								&times;
							</button>
						</div>
						<div style={{ padding: '20px' }}>
							{issueModalAnnotation.type === 'comment' ? (
								<div>
									<div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>
										{issueThreadForModal.title || issueModalAnnotation.title || 'Issue Thread'}
									</div>
									{issueThreadForModal.issue ? (
										<div style={{ border: '1px solid #f1b0b7', borderRadius: '8px', padding: '12px', marginBottom: '12px', background: '#fff5f6' }}>
											<div style={{ fontSize: '12px', color: '#dc3545', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Primary Issue</div>
											<div style={{ color: '#6c757d', marginBottom: '8px', fontSize: '12px' }}>
												{moment((issueThreadForModal.issue as any).date).format('DD MMM YYYY HH:mm')} | {issueThreadForModal.issue.author || 'Unknown'}
											</div>
											{issueThreadForModal.issue.description && (
												<div style={{ marginBottom: '8px', whiteSpace: 'pre-wrap' }}>{issueThreadForModal.issue.description}</div>
											)}
											{issueThreadForModal.issue.message && (
												<div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: issueThreadForModal.issue.message }} />
											)}
										</div>
									) : (
										<div style={{ border: '1px dashed #ced4da', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', color: '#6c757d' }}>
											Primary issue not available in current range.
										</div>
									)}

									<div style={{ fontSize: '12px', color: '#fd7e14', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>
										Comments ({issueThreadForModal.comments.length})
									</div>
									{issueThreadForModal.comments.length > 0 ? (
										<div style={{ display: 'grid', gap: '10px' }}>
											{issueThreadForModal.comments.map((c, idx) => (
												<div key={`${(c as any).date || idx}-${idx}`} style={{ border: '1px solid #ffe5c2', borderRadius: '8px', padding: '10px 12px', background: '#fffaf2' }}>
													<div style={{ color: '#6c757d', marginBottom: '6px', fontSize: '12px' }}>
														{moment((c as any).date).format('DD MMM YYYY HH:mm')} | {c.author || 'Unknown'}
													</div>
													{c.message && <div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: c.message }} />}
												</div>
											))}
										</div>
									) : (
										<div style={{ color: '#6c757d' }}>No comments found for this issue.</div>
									)}
								</div>
							) : (
								<div>
									<div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{issueModalAnnotation.title || 'Untitled'}</div>
									<div style={{ color: '#6c757d', marginBottom: '14px' }}>
										{moment((issueModalAnnotation as any).date).format('DD MMM YYYY HH:mm')} | {issueModalAnnotation.author || 'Unknown'}
									</div>
									{issueModalAnnotation.description && (
										<div style={{ marginBottom: '12px', whiteSpace: 'pre-wrap' }}>{issueModalAnnotation.description}</div>
									)}
									{issueModalAnnotation.message && (
										<div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: issueModalAnnotation.message }} />
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Maintenance Detail Modal */}
			{maintenanceModalReport && (
				<div
					style={{
						position: 'fixed', inset: 0, zIndex: 2000,
						background: 'rgba(0,0,0,0.55)',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						padding: '16px',
					}}
					onClick={() => setMaintenanceModalReport(null)}
				>
					<div
						style={{
							background: '#fff', borderRadius: '10px', maxWidth: '840px', width: '100%',
							maxHeight: '90vh', overflowY: 'auto',
							boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
							border: '2px solid #20c997',
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #dee2e6', background: '#20c997', borderRadius: '8px 8px 0 0' }}>
							<strong style={{ color: '#fff', fontSize: '16px' }}>Maintenance Report</strong>
							<button
								onClick={() => setMaintenanceModalReport(null)}
								style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', color: '#fff', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
								aria-label='Close'
							>
								&times;
							</button>
						</div>
						<div style={{ padding: '20px' }}>
							<div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{maintenanceModalReport.reportType || 'Maintenance'}</div>
							<div style={{ color: '#6c757d', marginBottom: '14px' }}>
								{moment(
									(maintenanceModalReport as any).reportDate ||
									(maintenanceModalReport as any).report_date ||
									(maintenanceModalReport as any).createdAt ||
									(maintenanceModalReport as any).created_at
								).format('DD MMM YYYY HH:mm')}
							</div>
							<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
								{[
									{ label: 'Report ID', value: (maintenanceModalReport as any).Id || (maintenanceModalReport as any).id },
									{ label: 'Station', value: (maintenanceModalReport as any).stationName || (maintenanceModalReport as any).stationId },
									{ label: 'Technician', value: (maintenanceModalReport as any).technicianDisplayname },
									{ label: 'Status', value: (maintenanceModalReport as any).status },
								].filter((f) => f.value).map(({ label, value }) => (
									<div key={label} style={{ background: '#f8f9fa', borderRadius: '6px', padding: '10px 12px' }}>
										<div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '3px' }}>{label}</div>
										<div style={{ fontSize: '13px', color: '#212529' }}>{String(value)}</div>
									</div>
								))}
							</div>
							{(() => {
								const maintenancePhotos = getMaintenancePhotos(maintenanceModalReport);
								return maintenancePhotos.length > 0 ? (
									<div style={{ marginTop: '16px' }}>
										<div style={{ fontSize: '12px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '10px' }}>Photos</div>
										<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
											{maintenancePhotos.map((photo, idx) => (
												<div key={`${photo.src}-${idx}`} style={{ borderRadius: '6px', overflow: 'hidden', border: '1px solid #dee2e6', textAlign: 'center' }}>
													<img
														src={photo.src}
														alt={photo.label}
														style={{ width: '100%', display: 'block', maxHeight: '160px', objectFit: 'cover' }}
													/>
													<div style={{ fontSize: '11px', color: '#6c757d', padding: '6px 4px', background: '#f8f9fa' }}>{photo.label}</div>
												</div>
											))}
										</div>
									</div>
								) : null;
							})()}
						</div>
					</div>
				</div>
			)}

			{/* ODK Submission Detail Modal */}
			{odkModalSubmission && (
			<div
				style={{
					position: 'fixed', inset: 0, zIndex: 2000,
					background: 'rgba(0,0,0,0.55)',
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					padding: '16px',
				}}
				onClick={() => setOdkModalSubmission(null)}
			>
				<div
					style={{
						background: '#fff', borderRadius: '10px', maxWidth: '780px', width: '100%',
						maxHeight: '90vh', overflowY: 'auto',
						boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
						border: '2px solid #17a2b8',
					}}
					onClick={(e) => e.stopPropagation()}
				>
					{/* Header */}
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #dee2e6', background: '#17a2b8', borderRadius: '8px 8px 0 0' }}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							<i className='fa fa-file-text' style={{ color: '#fff', fontSize: '18px' }} />
								<strong style={{ color: '#fff', fontSize: '16px' }}>Field Submission</strong>
								<span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
									{moment(
										(odkModalSubmission as any).submissionDate ||
										(odkModalSubmission as any).submission_date ||
										(odkModalSubmission as any).createdAt ||
										(odkModalSubmission as any).created_at ||
										(odkModalSubmission as any).today
									).format('DD MMM YYYY HH:mm')}
								</span>
						</div>
						<button
							onClick={() => setOdkModalSubmission(null)}
							style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', color: '#fff', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
							aria-label='Close'
						>
							&times;
						</button>
					</div>

					{/* Body */}
					<div style={{ padding: '20px' }}>
						{/* Key details grid */}
						<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
							{[
								{ label: 'Location Name', value: getSubmissionValue(odkModalSubmission, ['locationName', 'location_name']) },
								{ label: 'Station ID', value: getSubmissionValue(odkModalSubmission, ['stationId', 'station_id']) },
								{ label: 'Host Name', value: getSubmissionValue(odkModalSubmission, ['hostName', 'host_name']) },
								{ label: 'Date Recorded', value: getSubmissionValue(odkModalSubmission, ['today']) },
								{ label: 'Start', value: getSubmissionValue(odkModalSubmission, ['start']) },
								{ label: 'End', value: getSubmissionValue(odkModalSubmission, ['end']) },
								{ label: 'Encroachment', value: getSubmissionValue(odkModalSubmission, ['encroachment']) },
							].filter(f => f.value).map(({ label, value }) => (
								<div key={label} style={{ background: '#f8f9fa', borderRadius: '6px', padding: '10px 14px' }}>
									<div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '3px' }}>{label}</div>
									<div style={{ fontSize: '13px', color: '#212529' }}>{String(value)}</div>
								</div>
							))}
						</div>

						{/* Comment — full width */}
						{getSubmissionValue(odkModalSubmission, ['comment']) && (
							<div style={{ background: '#f8f9fa', borderRadius: '6px', padding: '12px 14px', marginBottom: '16px' }}>
								<div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Comment</div>
								<div style={{ fontSize: '13px', color: '#212529', whiteSpace: 'pre-wrap' }}>{String(getSubmissionValue(odkModalSubmission, ['comment']))}</div>
							</div>
						)}

						{/* Encroachment explanation — full width */}
						{getSubmissionValue(odkModalSubmission, ['explainEncroachment', 'explain_encroachment']) && (
							<div style={{ background: '#fff3cd', borderRadius: '6px', padding: '12px 14px', marginBottom: '16px', border: '1px solid #ffc107' }}>
								<div style={{ fontSize: '11px', color: '#856404', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Encroachment Explanation</div>
								<div style={{ fontSize: '13px', color: '#212529', whiteSpace: 'pre-wrap' }}>{String(getSubmissionValue(odkModalSubmission, ['explainEncroachment', 'explain_encroachment']))}</div>
							</div>
						)}
						{odkModalHydrating && (
							<div style={{ marginBottom: '12px', color: '#17a2b8', fontSize: '12px' }}>
								<i className='fa fa-spinner fa-spin me-1' />
								Loading submission images...
							</div>
						)}

						{/* Photos grid */}
						{(() => {
							const photos = [
								{ keys: ['stationPhoto', 'station_photo'], label: 'Station Photo' },
								{ keys: ['solarPanel', 'solar_panel'], label: 'Solar Panel' },
								{ keys: ['splashGuard', 'splash_guard'], label: 'Splash Guard' },
								{ keys: ['rainGauge', 'rain_gauge'], label: 'Rain Gauge' },
								{ keys: ['encroachmentImage', 'encroachment_image'], label: 'Encroachment' },
								{ keys: ['datalogger'], label: 'Datalogger' },
							]
								.map((p) => ({ ...p, value: getSubmissionValue(odkModalSubmission, p.keys) }))
								.filter((p) => p.value);
							return photos.length > 0 ? (
								<div>
									<div style={{ fontSize: '12px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '10px' }}>Photos</div>
									<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
										{photos.map(({ keys, label, value }) => (
												<div key={keys[0]} style={{ borderRadius: '6px', overflow: 'hidden', border: '1px solid #dee2e6', textAlign: 'center' }}>
													{resolveMediaSrc(value) ? (
														<img
															src={resolveMediaSrc(value) as string}
															alt={label}
															style={{ width: '100%', display: 'block', maxHeight: '160px', objectFit: 'cover' }}
														/>
													) : (
														<div style={{ padding: '24px 8px', color: '#6c757d', fontSize: '12px' }}>Image unavailable</div>
													)}
													<div style={{ fontSize: '11px', color: '#6c757d', padding: '6px 4px', background: '#f8f9fa' }}>{label}</div>
												</div>
										))}
									</div>
								</div>
							) : null;
						})()}
					</div>

					{/* Footer */}
					<div style={{ padding: '12px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end' }}>
						<button
							className='btn btn-secondary btn-sm'
							onClick={() => setOdkModalSubmission(null)}
						>
							<i className='fa fa-times me-1' />
							Close
						</button>
					</div>
				</div>
			</div>
		)}
		</>
	);
};

export default React.memo(SimpleTimeSeriesChart);
